/* global BigInt */
import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { 
  Button, TextField, Container, Paper, CircularProgress, 
  Alert, Typography, Box, Checkbox, FormControlLabel,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Switch, Slider, Chip, Collapse, Dialog,
  DialogTitle, DialogContent, DialogContentText, DialogActions,
  Select, MenuItem, InputLabel, FormControl, Grid, LinearProgress,
  IconButton, Snackbar
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// 定义已知需要特殊处理的代币列表（带有转账费或特殊转账机制）
const SPECIAL_TOKENS = {
  'HG': true,
  'HGDAO': true
};

// 创建一个简单的消息通知函数
const showMessage = (content, type = 'info', duration = 3000) => {
  const messageElement = document.createElement('div');
  messageElement.textContent = content;
  messageElement.style.position = 'fixed';
  messageElement.style.top = '20px';
  messageElement.style.left = '50%';
  messageElement.style.transform = 'translateX(-50%)';
  messageElement.style.padding = '10px 20px';
  messageElement.style.borderRadius = '4px';
  messageElement.style.zIndex = '1000';
  messageElement.style.boxShadow = '0 3px 6px rgba(0,0,0,0.2)';
  
  if (type === 'success') {
    messageElement.style.backgroundColor = '#52c41a';
    messageElement.style.color = '#fff';
  } else if (type === 'error') {
    messageElement.style.backgroundColor = '#ff4d4f';
    messageElement.style.color = '#fff';
  } else if (type === 'warning') {
    messageElement.style.backgroundColor = '#faad14';
    messageElement.style.color = '#fff';
  } else {
    messageElement.style.backgroundColor = '#1890ff';
    messageElement.style.color = '#fff';
  }
  
  document.body.appendChild(messageElement);
  
  setTimeout(() => {
    messageElement.style.opacity = '0';
    messageElement.style.transition = 'opacity 0.3s';
    setTimeout(() => {
      document.body.removeChild(messageElement);
    }, 300);
  }, duration);
};

// 创建自定义消息对象
const message = {
  success: (content, duration) => showMessage(content, 'success', duration),
  error: (content, duration) => showMessage(content, 'error', duration),
  warning: (content, duration) => showMessage(content, 'warning', duration),
  info: (content, duration) => showMessage(content, 'info', duration)
};

// ==================== 合约配置 ====================
const PANCAKE_ROUTER_V2 = '0x10ED43C718714eb63d5aA57B78B54704E256024E';
const PANCAKE_FACTORY_V2 = '0xcA143Ce32Fe78f1f7019d7d551a6402fC5350c73';
const WBNB_ADDRESS = '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c';
// 添加FIST-USDT交易对地址
const FIST_USDT_PAIR = '0x703f1C0B4399A51704e798002281bf26D6f9c2E6';

// ==================== 默认代币配置 ====================
const DEFAULT_TOKENS = {
  BNB: {
    address: WBNB_ADDRESS,
    decimals: 18,
    symbol: 'BNB'
  },
  USDT: {
    address: '0x55d398326f99059fF775485246999027B3197955',
    decimals: 18,
    symbol: 'USDT'
  },
  BUSD: {
    address: '0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56',
    decimals: 18,
    symbol: 'BUSD'
  },
  FIST: {
    address: '0xc9882def23bc42d53895b8361d0b1edc7570bc6a',
    decimals: 6,
    symbol: 'FIST'
  }
};

// 提供一个简单的方法用于在前端计算代币的USDT价值
export const calculateUSDTValueFromFIST = (tokenAmount, tokenPrice, fistUsdtRate) => {
  if (!tokenAmount || isNaN(tokenAmount) || tokenAmount <= 0) {
    return 0;
  }
  
  if (!tokenPrice || isNaN(tokenPrice) || tokenPrice <= 0) {
    return 0;
  }
  
  if (!fistUsdtRate || isNaN(fistUsdtRate) || fistUsdtRate <= 0) {
    return 0;
  }
  
  // 1. 计算代币的FIST价值
  const fistValue = tokenAmount * tokenPrice;
  
  // 2. 计算USDT价值
  return fistValue * fistUsdtRate;
};

// 全局变量，用于存储最新的FIST-USDT价格
let currentFistUsdtRate = '0';

// 获取当前FIST-USDT价格
export const getCurrentFistUsdtRate = () => {
  return currentFistUsdtRate;
};

// 提供一个异步函数，用于获取最新的FIST-USDT价格
export const fetchFistUsdtPrice = async (provider) => {
  try {
    // 使用固定的FIST-USDT交易对地址获取价格
    const pairContract = new ethers.Contract(FIST_USDT_PAIR, [
      "function getReserves() external view returns (uint112 reserve0, uint112 reserve1, uint32 blockTimestampLast)",
      "function token0() external view returns (address)"
    ], provider);
    
    const reserves = await pairContract.getReserves();
    const token0 = await pairContract.token0();
    
    // 确定哪个是FIST，哪个是USDT
    const isFistToken0 = token0.toLowerCase() === DEFAULT_TOKENS.FIST.address.toLowerCase();
    
    let fistPrice;
    if (isFistToken0) {
      // FIST是token0，USDT是token1
      fistPrice = ethers.formatUnits(
        (reserves.reserve1 * BigInt(10 ** DEFAULT_TOKENS.FIST.decimals)) / reserves.reserve0,
        DEFAULT_TOKENS.USDT.decimals
      );
    } else {
      // FIST是token1，USDT是token0
      fistPrice = ethers.formatUnits(
        (reserves.reserve0 * BigInt(10 ** DEFAULT_TOKENS.FIST.decimals)) / reserves.reserve1,
        DEFAULT_TOKENS.USDT.decimals
      );
    }
    
    // 更新全局变量
    currentFistUsdtRate = formatPrice(fistPrice);
    
    return formatPrice(fistPrice);
  } catch (err) {
    console.error("获取FIST-USDT价格失败:", err);
    return currentFistUsdtRate || '0';
  }
};

/**
 * 使用指南：如何在其他组件中使用FIST-USDT价格计算代币USDT价值
 * 
 * 方法一：直接访问全局变量（简单但不保证实时性）
 * 
 * import { getCurrentFistUsdtRate, calculateUSDTValueFromFIST } from './path/to/fist.js';
 * 
 * const tokenUsdtValue = calculateUSDTValueFromFIST(
 *   tokenAmount,           // 代币数量
 *   tokenToFistPrice,      // 代币对FIST的价格
 *   getCurrentFistUsdtRate() // 当前的FIST-USDT价格
 * );
 * 
 * 方法二：实时获取最新FIST-USDT价格（更准确但需要异步）
 * 
 * import { fetchFistUsdtPrice, calculateUSDTValueFromFIST } from './path/to/fist.js';
 * 
 * async function getTokenUsdtValue(provider, tokenAmount, tokenToFistPrice) {
 *   const fistUsdtRate = await fetchFistUsdtPrice(provider);
 *   return calculateUSDTValueFromFIST(tokenAmount, tokenToFistPrice, fistUsdtRate);
 * }
 * 
 * // 使用示例
 * getTokenUsdtValue(provider, tokenAmount, tokenToFistPrice).then(value => {
 *   console.log(`代币USDT价值: ${value}`);
 * });
 */

// ==================== 安全配置 ====================
// 移除 ADMIN_CONFIG 常量
// ... existing code ...
// 删除以下管理员配置
// const ADMIN_CONFIG = {
//   adminPasswords: ['bioach', 'cz76538', 'btygh23', 'hj67tyu', 'opti784', 'df73jkl', 'wekdjt', '123'], // 支持多个管理员密码
//   adminAddress: '0x539c7b6b3e439b1016e476af9eaa1f92f9bf7d8f' // 替换为实际管理员地址
// };

// ==================== RPC配置 ====================
const RPC_URLS = [
  { name: 'Binance', url: 'https://bsc-mainnet.core.chainstack.com/2434de43e2d44be664023ad94d301294' },
  { name: 'Binance', url: 'https://bsc-dataseed.binance.org/' },
  { name: 'Defibit', url: 'https://bsc-dataseed1.defibit.io/' },
  { name: 'Ninicoin', url: 'https://bsc-dataseed1.ninicoin.io/' },
  { name: 'BSC', url: 'https://bscrpc.com' },
  { name: '1', url: 'https://rpc.ankr.com/bsc' },
  { name: '2', url: 'https://binance.nodereal.io' },
  { name: '3', url: 'https://rpc-bsc.bnb48.club' }
];

// ==================== ABIs ====================
const ERC20_ABI = [
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
  "function balanceOf(address) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)"
];

const PANCAKE_ROUTER_ABI = [{
	"inputs": [{
		"internalType": "address",
		"name": "_factory",
		"type": "address"
	}, {
		"internalType": "address",
		"name": "_WETH",
		"type": "address"
	}],
	"stateMutability": "nonpayable",
	"type": "constructor"
}, {
	"inputs": [],
	"name": "WETH",
	"outputs": [{
		"internalType": "address",
		"name": "",
		"type": "address"
	}],
	"stateMutability": "view",
	"type": "function"
}, {
	"inputs": [{
		"internalType": "address",
		"name": "tokenA",
		"type": "address"
	}, {
		"internalType": "address",
		"name": "tokenB",
		"type": "address"
	}, {
		"internalType": "uint256",
		"name": "amountADesired",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountBDesired",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountAMin",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountBMin",
		"type": "uint256"
	}, {
		"internalType": "address",
		"name": "to",
		"type": "address"
	}, {
		"internalType": "uint256",
		"name": "deadline",
		"type": "uint256"
	}],
	"name": "addLiquidity",
	"outputs": [{
		"internalType": "uint256",
		"name": "amountA",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountB",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "liquidity",
		"type": "uint256"
	}],
	"stateMutability": "nonpayable",
	"type": "function"
}, {
	"inputs": [{
		"internalType": "address",
		"name": "token",
		"type": "address"
	}, {
		"internalType": "uint256",
		"name": "amountTokenDesired",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountTokenMin",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountETHMin",
		"type": "uint256"
	}, {
		"internalType": "address",
		"name": "to",
		"type": "address"
	}, {
		"internalType": "uint256",
		"name": "deadline",
		"type": "uint256"
	}],
	"name": "addLiquidityETH",
	"outputs": [{
		"internalType": "uint256",
		"name": "amountToken",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountETH",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "liquidity",
		"type": "uint256"
	}],
	"stateMutability": "payable",
	"type": "function"
}, {
	"inputs": [],
	"name": "factory",
	"outputs": [{
		"internalType": "address",
		"name": "",
		"type": "address"
	}],
	"stateMutability": "view",
	"type": "function"
}, {
	"inputs": [{
		"internalType": "uint256",
		"name": "amountOut",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "reserveIn",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "reserveOut",
		"type": "uint256"
	}],
	"name": "getAmountIn",
	"outputs": [{
		"internalType": "uint256",
		"name": "amountIn",
		"type": "uint256"
	}],
	"stateMutability": "pure",
	"type": "function"
}, {
	"inputs": [{
		"internalType": "uint256",
		"name": "amountIn",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "reserveIn",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "reserveOut",
		"type": "uint256"
	}],
	"name": "getAmountOut",
	"outputs": [{
		"internalType": "uint256",
		"name": "amountOut",
		"type": "uint256"
	}],
	"stateMutability": "pure",
	"type": "function"
}, {
	"inputs": [{
		"internalType": "uint256",
		"name": "amountOut",
		"type": "uint256"
	}, {
		"internalType": "address[]",
		"name": "path",
		"type": "address[]"
	}],
	"name": "getAmountsIn",
	"outputs": [{
		"internalType": "uint256[]",
		"name": "amounts",
		"type": "uint256[]"
	}],
	"stateMutability": "view",
	"type": "function"
}, {
	"inputs": [{
		"internalType": "uint256",
		"name": "amountIn",
		"type": "uint256"
	}, {
		"internalType": "address[]",
		"name": "path",
		"type": "address[]"
	}],
	"name": "getAmountsOut",
	"outputs": [{
		"internalType": "uint256[]",
		"name": "amounts",
		"type": "uint256[]"
	}],
	"stateMutability": "view",
	"type": "function"
}, {
	"inputs": [{
		"internalType": "uint256",
		"name": "amountA",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "reserveA",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "reserveB",
		"type": "uint256"
	}],
	"name": "quote",
	"outputs": [{
		"internalType": "uint256",
		"name": "amountB",
		"type": "uint256"
	}],
	"stateMutability": "pure",
	"type": "function"
}, {
	"inputs": [{
		"internalType": "address",
		"name": "tokenA",
		"type": "address"
	}, {
		"internalType": "address",
		"name": "tokenB",
		"type": "address"
	}, {
		"internalType": "uint256",
		"name": "liquidity",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountAMin",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountBMin",
		"type": "uint256"
	}, {
		"internalType": "address",
		"name": "to",
		"type": "address"
	}, {
		"internalType": "uint256",
		"name": "deadline",
		"type": "uint256"
	}],
	"name": "removeLiquidity",
	"outputs": [{
		"internalType": "uint256",
		"name": "amountA",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountB",
		"type": "uint256"
	}],
	"stateMutability": "nonpayable",
	"type": "function"
}, {
	"inputs": [{
		"internalType": "address",
		"name": "token",
		"type": "address"
	}, {
		"internalType": "uint256",
		"name": "liquidity",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountTokenMin",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountETHMin",
		"type": "uint256"
	}, {
		"internalType": "address",
		"name": "to",
		"type": "address"
	}, {
		"internalType": "uint256",
		"name": "deadline",
		"type": "uint256"
	}],
	"name": "removeLiquidityETH",
	"outputs": [{
		"internalType": "uint256",
		"name": "amountToken",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountETH",
		"type": "uint256"
	}],
	"stateMutability": "nonpayable",
	"type": "function"
}, {
	"inputs": [{
		"internalType": "address",
		"name": "token",
		"type": "address"
	}, {
		"internalType": "uint256",
		"name": "liquidity",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountTokenMin",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountETHMin",
		"type": "uint256"
	}, {
		"internalType": "address",
		"name": "to",
		"type": "address"
	}, {
		"internalType": "uint256",
		"name": "deadline",
		"type": "uint256"
	}, {
		"internalType": "bool",
		"name": "approveMax",
		"type": "bool"
	}, {
		"internalType": "uint8",
		"name": "v",
		"type": "uint8"
	}, {
		"internalType": "bytes32",
		"name": "r",
		"type": "bytes32"
	}, {
		"internalType": "bytes32",
		"name": "s",
		"type": "bytes32"
	}],
	"name": "removeLiquidityETHWithPermit",
	"outputs": [{
		"internalType": "uint256",
		"name": "amountToken",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountETH",
		"type": "uint256"
	}],
	"stateMutability": "nonpayable",
	"type": "function"
}, {
	"inputs": [{
		"internalType": "address",
		"name": "token",
		"type": "address"
	}, {
		"internalType": "uint256",
		"name": "liquidity",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountTokenMin",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountETHMin",
		"type": "uint256"
	}, {
		"internalType": "address",
		"name": "to",
		"type": "address"
	}, {
		"internalType": "uint256",
		"name": "deadline",
		"type": "uint256"
	}, {
		"internalType": "bool",
		"name": "approveMax",
		"type": "bool"
	}, {
		"internalType": "uint8",
		"name": "v",
		"type": "uint8"
	}, {
		"internalType": "bytes32",
		"name": "r",
		"type": "bytes32"
	}, {
		"internalType": "bytes32",
		"name": "s",
		"type": "bytes32"
	}],
	"name": "removeLiquidityETHWithPermitSupportingFeeOnTransferTokens",
	"outputs": [{
		"internalType": "uint256",
		"name": "amountETH",
		"type": "uint256"
	}],
	"stateMutability": "nonpayable",
	"type": "function"
}, {
	"inputs": [{
		"internalType": "address",
		"name": "tokenA",
		"type": "address"
	}, {
		"internalType": "address",
		"name": "tokenB",
		"type": "address"
	}, {
		"internalType": "uint256",
		"name": "liquidity",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountAMin",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountBMin",
		"type": "uint256"
	}, {
		"internalType": "address",
		"name": "to",
		"type": "address"
	}, {
		"internalType": "uint256",
		"name": "deadline",
		"type": "uint256"
	}, {
		"internalType": "bool",
		"name": "approveMax",
		"type": "bool"
	}, {
		"internalType": "uint8",
		"name": "v",
		"type": "uint8"
	}, {
		"internalType": "bytes32",
		"name": "r",
		"type": "bytes32"
	}, {
		"internalType": "bytes32",
		"name": "s",
		"type": "bytes32"
	}],
	"name": "removeLiquidityWithPermit",
	"outputs": [{
		"internalType": "uint256",
		"name": "amountA",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountB",
		"type": "uint256"
	}],
	"stateMutability": "nonpayable",
	"type": "function"
}, {
	"inputs": [{
		"internalType": "uint256",
		"name": "amountOut",
		"type": "uint256"
	}, {
		"internalType": "address[]",
		"name": "path",
		"type": "address[]"
	}, {
		"internalType": "address",
		"name": "to",
		"type": "address"
	}, {
		"internalType": "uint256",
		"name": "deadline",
		"type": "uint256"
	}],
	"name": "swapETHForExactTokens",
	"outputs": [{
		"internalType": "uint256[]",
		"name": "amounts",
		"type": "uint256[]"
	}],
	"stateMutability": "payable",
	"type": "function"
}, {
	"inputs": [{
		"internalType": "uint256",
		"name": "amountOutMin",
		"type": "uint256"
	}, {
		"internalType": "address[]",
		"name": "path",
		"type": "address[]"
	}, {
		"internalType": "address",
		"name": "to",
		"type": "address"
	}, {
		"internalType": "uint256",
		"name": "deadline",
		"type": "uint256"
	}],
	"name": "swapExactETHForTokens",
	"outputs": [{
		"internalType": "uint256[]",
		"name": "amounts",
		"type": "uint256[]"
	}],
	"stateMutability": "payable",
	"type": "function"
}, {
	"inputs": [{
		"internalType": "uint256",
		"name": "amountOutMin",
		"type": "uint256"
	}, {
		"internalType": "address[]",
		"name": "path",
		"type": "address[]"
	}, {
		"internalType": "address",
		"name": "to",
		"type": "address"
	}, {
		"internalType": "uint256",
		"name": "deadline",
		"type": "uint256"
	}],
	"name": "swapExactETHForTokensSupportingFeeOnTransferTokens",
	"outputs": [],
	"stateMutability": "payable",
	"type": "function"
}, {
	"inputs": [{
		"internalType": "uint256",
		"name": "amountIn",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountOutMin",
		"type": "uint256"
	}, {
		"internalType": "address[]",
		"name": "path",
		"type": "address[]"
	}, {
		"internalType": "address",
		"name": "to",
		"type": "address"
	}, {
		"internalType": "uint256",
		"name": "deadline",
		"type": "uint256"
	}],
	"name": "swapExactTokensForETH",
	"outputs": [{
		"internalType": "uint256[]",
		"name": "amounts",
		"type": "uint256[]"
	}],
	"stateMutability": "nonpayable",
	"type": "function"
}, {
	"inputs": [{
		"internalType": "uint256",
		"name": "amountIn",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountOutMin",
		"type": "uint256"
	}, {
		"internalType": "address[]",
		"name": "path",
		"type": "address[]"
	}, {
		"internalType": "address",
		"name": "to",
		"type": "address"
	}, {
		"internalType": "uint256",
		"name": "deadline",
		"type": "uint256"
	}],
	"name": "swapExactTokensForETHSupportingFeeOnTransferTokens",
	"outputs": [],
	"stateMutability": "nonpayable",
	"type": "function"
}, {
	"inputs": [{
		"internalType": "uint256",
		"name": "amountIn",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountOutMin",
		"type": "uint256"
	}, {
		"internalType": "address[]",
		"name": "path",
		"type": "address[]"
	}, {
		"internalType": "address",
		"name": "to",
		"type": "address"
	}, {
		"internalType": "uint256",
		"name": "deadline",
		"type": "uint256"
	}],
	"name": "swapExactTokensForTokens",
	"outputs": [{
		"internalType": "uint256[]",
		"name": "amounts",
		"type": "uint256[]"
	}],
	"stateMutability": "nonpayable",
	"type": "function"
}, {
	"inputs": [{
		"internalType": "uint256",
		"name": "amountIn",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountOutMin",
		"type": "uint256"
	}, {
		"internalType": "address[]",
		"name": "path",
		"type": "address[]"
	}, {
		"internalType": "address",
		"name": "to",
		"type": "address"
	}, {
		"internalType": "uint256",
		"name": "deadline",
		"type": "uint256"
	}],
	"name": "swapExactTokensForTokensSupportingFeeOnTransferTokens",
	"outputs": [],
	"stateMutability": "nonpayable",
	"type": "function"
}, {
	"inputs": [{
		"internalType": "uint256",
		"name": "amountOut",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountInMax",
		"type": "uint256"
	}, {
		"internalType": "address[]",
		"name": "path",
		"type": "address[]"
	}, {
		"internalType": "address",
		"name": "to",
		"type": "address"
	}, {
		"internalType": "uint256",
		"name": "deadline",
		"type": "uint256"
	}],
	"name": "swapTokensForExactETH",
	"outputs": [{
		"internalType": "uint256[]",
		"name": "amounts",
		"type": "uint256[]"
	}],
	"stateMutability": "nonpayable",
	"type": "function"
}, {
	"inputs": [{
		"internalType": "uint256",
		"name": "amountOut",
		"type": "uint256"
	}, {
		"internalType": "uint256",
		"name": "amountInMax",
		"type": "uint256"
	}, {
		"internalType": "address[]",
		"name": "path",
		"type": "address[]"
	}, {
		"internalType": "address",
		"name": "to",
		"type": "address"
	}, {
		"internalType": "uint256",
		"name": "deadline",
		"type": "uint256"
	}],
	"name": "swapTokensForExactTokens",
	"outputs": [{
		"internalType": "uint256[]",
		"name": "amounts",
		"type": "uint256[]"
	}],
	"stateMutability": "nonpayable",
	"type": "function"
}, {
	"stateMutability": "payable",
	"type": "receive"
}];

const PANCAKE_FACTORY_ABI = [
  "function getPair(address tokenA, address tokenB) external view returns (address pair)"
];

// ==================== 工具函数 ====================
function formatBigInt(value, decimals = 18) {
  if (!value || value.toString() === '0') return '0';
  try {
    return ethers.formatUnits(value, decimals);
  } catch (err) {
    console.error("格式化BigInt失败:", err);
    return '0';
  }
}

function getRandomAmount(min, max, decimals = 6) {
  // 参数验证
  const minValue = parseFloat(min);
  const maxValue = parseFloat(max);
  
  // 检查参数有效性
  if (isNaN(minValue) || isNaN(maxValue)) {
    console.error(`getRandomAmount收到无效参数: min=${min}, max=${max}`);
    return 0.01; // 返回安全默认值
  }
  
  // 确保min < max
  const actualMin = Math.min(minValue, maxValue);
  const actualMax = Math.max(minValue, maxValue);
  
  // 生成随机数
  const random = Math.random() * (actualMax - actualMin) + actualMin;
  
  // 格式化到指定小数位数
  const result = parseFloat(random.toFixed(decimals));
  
  // 最终检查，确保返回有效数字
  return isNaN(result) || result <= 0 ? actualMin : result;
}

// 修改formatPrice函数
function formatPrice(price) {
  if (!price) return '0';
  if (parseFloat(price) === 0) return '0';

  try {
    const str = price.toString();
    
    // 处理科学计数法
    if (str.includes('e')) {
      const [base, exponent] = str.split('e');
      const exp = parseInt(exponent);
      const sign = base.startsWith('-') ? '-' : '';
      const baseStr = base.replace('.', '').replace('-', '');
      
      if (exp < 0) {
        // 处理小数
        const absExp = Math.abs(exp);
        
        // 找到基数中的小数点位置
        const dotIndex = base.indexOf('.');
        const baseDecimals = dotIndex === -1 ? 0 : base.length - dotIndex - 1;
        
        // 计算需要补充的0的数量
        const zerosNeeded = absExp - baseDecimals;
        
        // 构建结果
        if (zerosNeeded > 0) {
          return `${sign}0.${'0'.repeat(zerosNeeded)}${baseStr}`;
        } else {
          // 如果基数已经有足够的小数位，直接格式化
          const num = parseFloat(base) * Math.pow(10, exp);
          return num.toString();
        }
      } else {
        // 处理大数
        return (parseFloat(base) * Math.pow(10, exp)).toString();
      }
    }
    
    // 对于普通数字，直接返回原始字符串以保持格式
    return str;
    
  } catch (err) {
    console.error('格式化价格失败:', err);
    return price.toString();
  }
}

async function getBNBBalance(provider, address) {
  try {
    const balance = await provider.getBalance(address);
    return ethers.formatEther(balance);
  } catch (err) {
    console.error("获取BNB余额失败:", err);
    return "0";
  }
}

async function transferBNBFromOtherWallet(provider, fromWallet, toAddress, requiredAmount) {
  try {
    const tx = await fromWallet.sendTransaction({
      to: toAddress,
      value: ethers.parseEther(requiredAmount.toString()),
      gasLimit: 21000
    });
    
    await tx.wait();
    console.log(`从钱包 ${fromWallet.address} 转入 ${requiredAmount} BNB 到 ${toAddress}`);
    return true;
  } catch (err) {
    console.error("BNB转账失败:", err);
    return false;
  }
}

// 添加交易对配置
const TRADING_PAIRS = [
  { symbol: 'BNB', address: DEFAULT_TOKENS.BNB.address, decimals: 18 },
  { symbol: 'USDT', address: DEFAULT_TOKENS.USDT.address, decimals: 18 },
  { symbol: 'BUSD', address: DEFAULT_TOKENS.BUSD.address, decimals: 18 },
  { symbol: 'FIST', address: '0xc9882def23bc42d53895b8361d0b1edc7570bc6a', decimals: 6 },
  { symbol: 'OSK', address: '0x8b5d3a1d2eebbe91c6f5781c37c1c0b9b7a9e8d7', decimals: 18 }
];

// 导出为独立工具函数
export const getFistUsdtValue = async (provider, amount) => {
  try {
    // 使用固定的FIST-USDT交易对地址获取价格
    const pairContract = new ethers.Contract(FIST_USDT_PAIR, [
      "function getReserves() external view returns (uint112 reserve0, uint112 reserve1, uint32 blockTimestampLast)",
      "function token0() external view returns (address)"
    ], provider);
    
    const reserves = await pairContract.getReserves();
    const token0 = await pairContract.token0();
    
    // 确定哪个是FIST，哪个是USDT
    const isFistToken0 = token0.toLowerCase() === DEFAULT_TOKENS.FIST.address.toLowerCase();
    
    // 计算1 FIST的USDT价格
    let fistPrice;
    let fistReserve, usdtReserve;
    
    if (isFistToken0) {
      // FIST是token0，USDT是token1
      fistReserve = reserves.reserve0;
      usdtReserve = reserves.reserve1;
    } else {
      // FIST是token1，USDT是token0
      fistReserve = reserves.reserve1;
      usdtReserve = reserves.reserve0;
    }
    
    // 计算实际价格（考虑价格影响）
    if (amount && !isNaN(amount) && amount > 0) {
      // 计算卖出amount数量的FIST得到的USDT数量
      const amountInWei = ethers.parseUnits(amount.toString(), DEFAULT_TOKENS.FIST.decimals);
      
      // 使用恒定乘积公式: (x + dx) * (y - dy) = x * y
      // 计算dy: dy = y * dx / (x + dx)
      const amountOut = (usdtReserve * amountInWei) / (fistReserve + amountInWei);
      
      // 格式化输出
      return ethers.formatUnits(amountOut, DEFAULT_TOKENS.USDT.decimals);
    } else {
      // 简单返回当前市场价格
      fistPrice = ethers.formatUnits(
        (usdtReserve * BigInt(10 ** DEFAULT_TOKENS.FIST.decimals)) / fistReserve,
        DEFAULT_TOKENS.USDT.decimals
      );
      return fistPrice;
    }
  } catch (err) {
    console.error("计算FIST-USDT价值失败:", err);
    return '0';
  }
};

// 通过FIST计算任何代币的USDT价值
export const getTokenUsdtValueViaFist = async (provider, tokenAddress, tokenAmount, tokenDecimals) => {
  try {
    if (!tokenAmount || isNaN(tokenAmount) || tokenAmount <= 0) {
      return 0;
    }
    
    // 1. 获取代币对FIST的价格
    const router = new ethers.Contract(PANCAKE_ROUTER_V2, PANCAKE_ROUTER_ABI, provider);
    const path = [tokenAddress, DEFAULT_TOKENS.FIST.address];
    
    // 计算1个代币能换多少FIST
    const amounts = await router.getAmountsOut(
      ethers.parseUnits('1', tokenDecimals), 
      path
    );
    
    const tokenToFistRate = ethers.formatUnits(amounts[1], DEFAULT_TOKENS.FIST.decimals);
    
    // 2. 计算代币的FIST价值
    const fistValue = parseFloat(tokenAmount) * parseFloat(tokenToFistRate);
    
    // 3. 获取FIST-USDT价格
    const fistUsdtRate = await getFistUsdtValue(provider);
    
    // 4. 计算USDT价值
    return fistValue * parseFloat(fistUsdtRate);
  } catch (err) {
    console.error("通过FIST计算代币USDT价值失败:", err);
    return 0;
  }
};

function PancakeV2BatchSwap() {
  // ==================== 状态管理 ====================
  const [provider, setProvider] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [selectedWallets, setSelectedWallets] = useState([]);
  // 删除 adminMode 状态
  // const [adminMode, setAdminMode] = useState(false);
  // 删除 password 状态，因为不再需要管理员登录
  // const [password, setPassword] = useState('');
  const [connectedAddress, setConnectedAddress] = useState('');
  const [currentRpcUrl, setCurrentRpcUrl] = useState(RPC_URLS[0].url);
  const [tokenAddress, setTokenAddress] = useState('');
  const [tokenInfo, setTokenInfo] = useState({ name: '', symbol: '', decimals: 18 });
  const [privateKeys, setPrivateKeys] = useState('');
  const [showSecurityWarning, setShowSecurityWarning] = useState(false);
  const [totalBalances, setTotalBalances] = useState({ bnb: '0', token: '0', fist: '0', tokenUsdtValue: '0' });
  const [priceInfo, setPriceInfo] = useState({
    price: '0',
    usdtPrice: '0',
    liquidity: '0',
    pairToken: '',
    status: '',
    bnbUsdtRate: '0',
    fistUsdtRate: '0' // 添加FIST-USDT比率
  });
  
  // 添加交易币种选择状态
  const [buyToken, setBuyToken] = useState('FIST'); // 默认使用FIST购买
  const [sellToken, setSellToken] = useState('FIST'); // 默认获得FIST
  const [volumeBuyToken, setVolumeBuyToken] = useState('FIST'); // 做市交易默认使用FIST购买
  const [volumeSellToken, setVolumeSellToken] = useState('FIST'); // 做市交易默认获得FIST
  
  const [buyAmount, setBuyAmount] = useState('0.01');
  const [buyAmountRange, setBuyAmountRange] = useState({ min: '0.01', max: '0.02' });
  const [useRandomBuyAmount, setUseRandomBuyAmount] = useState(false);
  const [buySequentialMode, setBuySequentialMode] = useState(false);
  const [buyDelayBetweenTx, setBuyDelayBetweenTx] = useState(5);
  const [buySlippage, setBuySlippage] = useState('1.0');
  const [sellAmount, setSellAmount] = useState('100');
  const [sellAmountRange, setSellAmountRange] = useState({ min: '0.01', max: '0.02' });
  const [useRandomSellAmount, setUseRandomSellAmount] = useState(false);
  const [sellSequentialMode, setSellSequentialMode] = useState(false);
  const [sellDelayBetweenTx, setSellDelayBetweenTx] = useState(5);
  const [sellSlippage, setSellSlippage] = useState('1.0');
  const [showVolumeSettings, setShowVolumeSettings] = useState(false);
  const [buyPercentage, setBuyPercentage] = useState(50);
  const [sellPercentage, setSellPercentage] = useState(50);
  const [isVolumeTrading, setIsVolumeTrading] = useState(false);
  const [volumeRounds, setVolumeRounds] = useState(1);
  const [currentRound, setCurrentRound] = useState(0);
  const [volumeTradeStatus, setVolumeTradeStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTx, setActiveTx] = useState(null);
  const [txHistory, setTxHistory] = useState([]);
  const [error, setError] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [volumeBuyAmount, setVolumeBuyAmount] = useState('0.01');
  const [volumeSellAmount, setVolumeSellAmount] = useState('0.01');
  const [volumeBuyAmountRange, setVolumeBuyAmountRange] = useState({ min: '0.01', max: '0.02' });
  const [volumeSellAmountRange, setVolumeSellAmountRange] = useState({ min: '0.01', max: '0.02' });
  const [useVolumeRandomBuyAmount, setUseVolumeRandomBuyAmount] = useState(false);
  const [useVolumeRandomSellAmount, setUseVolumeRandomSellAmount] = useState(false);
  const [volumeBuySlippage, setVolumeBuySlippage] = useState('1.0');
  const [volumeSellSlippage, setVolumeSellSlippage] = useState('1.0');
  const [volumeBuySequentialMode, setVolumeBuySequentialMode] = useState(false);
  const [volumeSellSequentialMode, setVolumeSellSequentialMode] = useState(false);
  const [volumeBuyDelayBetweenTx, setVolumeBuyDelayBetweenTx] = useState(5);
  const [volumeSellDelayBetweenTx, setVolumeSellDelayBetweenTx] = useState(5);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [currentActiveTx, setCurrentActiveTx] = useState(null);
  const [showVolumeTradeResults, setShowVolumeTradeResults] = useState(false);
  const [transactionStatus, setTransactionStatus] = useState({
    isProcessing: false,
    currentOperation: '',
    progress: 0,
    total: 0,
    successCount: 0,
    failCount: 0,
    lastError: null
  });
  const [selectedPair, setSelectedPair] = useState(TRADING_PAIRS[0]);
  const [rpcProvider, setRpcProvider] = useState(null);
  
  // 添加缺失的状态变量
  const [volumeTradingActive, setVolumeTradingActive] = useState(false);
  const [volumeSequentialMode, setVolumeSequentialMode] = useState(false);
  const [volumeDelayBetweenTx, setVolumeDelayBetweenTx] = useState(0);
  const [volumeSlippage, setVolumeSlippage] = useState(5);
  const [volumeAmount, setVolumeAmount] = useState(0.01);
  const [volumeAmountRange, setVolumeAmountRange] = useState(0.005);
  const [useRandomVolumeAmount, setUseRandomVolumeAmount] = useState(false);
  const [volumeInterval, setVolumeInterval] = useState(60);

  // 添加独立的loading状态
  const [connectLoading, setConnectLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [updateBalanceLoading, setUpdateBalanceLoading] = useState(false);
  const [fetchTokenLoading, setFetchTokenLoading] = useState(false);
  const [batchBuyLoading, setBatchBuyLoading] = useState(false);
  const [batchSellLoading, setBatchSellLoading] = useState(false);
  const [volumeTradingLoading, setVolumeTradingLoading] = useState(false);

  // 在组件内添加RPC选择状态
  const [selectedRpc, setSelectedRpc] = useState(RPC_URLS[0]);

  // 添加GAS相关状态
  const [customGasPrice, setCustomGasPrice] = useState('');
  const [useCustomGas, setUseCustomGas] = useState(false);

  // 在 [rpcProvider, setRpcProvider] = useState(null); 之后添加以下状态变量
  const [volumeBuyPercentage, setVolumeBuyPercentage] = useState('50');
  const [volumeSellPercentage, setVolumeSellPercentage] = useState('50');
  const [volumeTradeResults, setVolumeTradeResults] = useState({
    isRunning: false,
    totalBought: 0,
    totalSold: 0,
    totalTransactions: 0,
    roundsCompleted: 0,
    startTime: null,
    endTime: null,
    duration: '',
    buyBatchResults: [],
    sellBatchResults: [],
    error: null
  });
  const [volumeBuySequential, setVolumeBuySequential] = useState(false);
  const [volumeSellSequential, setVolumeSellSequential] = useState(false);
  const [volumeBuyRandom, setVolumeBuyRandom] = useState(false);
  const [minBuyAmount, setMinBuyAmount] = useState('0.01');
  const [maxBuyAmount, setMaxBuyAmount] = useState('0.05');
  const [minDelay, setMinDelay] = useState(1000);
  const [maxDelay, setMaxDelay] = useState(3000);
  const [minRoundDelay, setMinRoundDelay] = useState(5000);
  const [maxRoundDelay, setMaxRoundDelay] = useState(15000);
  const [shouldStopVolumeTrading, setShouldStopVolumeTrading] = useState(false);

  // 在状态管理部分添加一个新的状态变量用于交易暂停
  const [transactionsPaused, setTransactionsPaused] = useState(false);

  // 添加resetVolumeTradeResults函数
  const resetVolumeTradeResults = () => {
    setVolumeTradeResults({
      isRunning: false,
      totalBought: 0,
      totalSold: 0,
      totalTransactions: 0,
      roundsCompleted: 0,
      startTime: null,
      endTime: null,
      duration: '',
      buyBatchResults: [],
      sellBatchResults: [],
      error: null
    });
  };

  // ==================== 核心函数 ====================
  // 删除 checkAdminStatus 函数
  // const checkAdminStatus = (address) => {...}

  // 修改 connectWallet 函数，移除管理员检查
  const connectWallet = async () => {
    if (window.ethereum) {
      try {
        setConnectLoading(true);
        const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
        setConnectedAddress(accounts[0]);
        
        // 如果用户连接钱包了，也可以设置provider
        if (!provider) {
          const newProvider = new ethers.BrowserProvider(window.ethereum);
          setProvider(newProvider);
        }
        
        message.success('钱包连接成功，可用于签名和发送交易');
      } catch (err) {
        setError("用户拒绝连接钱包");
      } finally {
        setConnectLoading(false);
      }
    } else {
      setError("未检测到 MetaMask 或其他钱包扩展，但您仍然可以使用RPC功能和私钥导入功能");
    }
  };

  // 删除 handleAdminLogin 函数
  // const handleAdminLogin = () => {...}

  // 添加provider初始化
  useEffect(() => {
    const initProvider = async () => {
      try {
        if (window.ethereum) {
          const provider = new ethers.BrowserProvider(window.ethereum);
          setProvider(provider);
        } else {
          setError("未检测到 MetaMask 或其他钱包扩展");
        }
      } catch (err) {
        console.error("初始化provider失败:", err);
        setError("初始化provider失败");
      }
    };

    initProvider();
  }, []);

  // 初始化RPC Provider
  useEffect(() => {
    const initRpcProvider = async () => {
      try {
        const provider = new ethers.JsonRpcProvider(currentRpcUrl);
        setRpcProvider(provider);
      } catch (err) {
        console.error("初始化RPC Provider失败:", err);
        setError("初始化RPC Provider失败");
      }
    };

    initRpcProvider();
  }, [currentRpcUrl]);

  // 修改fetchTokenInfo函数
  const fetchTokenInfo = async () => {
    if (!rpcProvider) {
      setError("RPC Provider未初始化");
      return;
    }

    if (!tokenAddress) {
      setError("请输入代币地址");
      return;
    }

    if (!ethers.isAddress(tokenAddress)) {
      setError("无效的代币地址");
      return;
    }
    
    try {
      setFetchTokenLoading(true);
      setError(null);
      
      const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, rpcProvider);
      const [name, symbol, decimals] = await Promise.all([
        tokenContract.name(),
        tokenContract.symbol(),
        tokenContract.decimals()
      ]);
      
      setTokenInfo({ name, symbol, decimals: Number(decimals) });
      
      // 获取价格信息
      await fetchPriceAndLiquidity();
      
      if (wallets.length > 0) {
        await fetchWalletsBalances(wallets);
      }

      console.log("代币信息获取成功:", { name, symbol, decimals });
    } catch (err) {
      console.error("获取代币信息失败:", err);
      setError(`获取代币信息失败: ${err.message}`);
      setTokenInfo({ name: '', symbol: '', decimals: 18 });
    } finally {
      setFetchTokenLoading(false);
    }
  };

  // 添加计算总余额的函数
  const calculateTotalBalances = (walletsList) => {
    if (!walletsList || walletsList.length === 0 || !tokenInfo.symbol) {
      console.log('没有可用的钱包数据或代币信息未初始化');
      return;
    }

    const totals = {
      bnb: 0,
      token: 0,
      fist: 0,
      tokenUsdtValue: 0
    };

    walletsList.forEach(wallet => {
      // 累加BNB余额
      totals.bnb += parseFloat(wallet.bnbBalance || '0');
      
      // 累加代币余额
      totals.token += parseFloat(wallet.tokenBalance || '0');
      
      // 累加FIST余额
      totals.fist += parseFloat(wallet.fistBalance || '0');
      
      // 累加USDT价值
      const walletTokenUsdtValue = calculateTokenUsdtValue(
        parseFloat(wallet.tokenBalance || '0'),
        tokenInfo.symbol,
        selectedPair.symbol,
        priceInfo
      );
      totals.tokenUsdtValue += walletTokenUsdtValue;
    });

    // 设置总余额，使用统一的属性名称
    setTotalBalances({
      bnb: totals.bnb.toFixed(6),
      token: totals.token.toFixed(tokenInfo.decimals || 6),
      fist: totals.fist.toFixed(6),
      tokenUsdtValue: totals.tokenUsdtValue.toFixed(2)
    });
  };

  // 添加计算代币USDT价值的函数
  const calculateTokenUsdtValue = (tokenAmount, tokenSymbol, pairSymbol, priceInfo) => {
    if (!tokenAmount || isNaN(tokenAmount) || tokenAmount <= 0) {
      return 0;
    }
    
    // 如果已经有USDT价格，直接使用
    if (priceInfo.usdtPrice && parseFloat(priceInfo.usdtPrice) > 0) {
      return tokenAmount * parseFloat(priceInfo.usdtPrice);
    }
    
    // 如果交易对是FIST，则通过FIST计算USDT价值
    if (pairSymbol === 'FIST' && priceInfo.price && priceInfo.fistUsdtRate) {
      const valueInFist = tokenAmount * parseFloat(priceInfo.price);
      return valueInFist * parseFloat(priceInfo.fistUsdtRate);
    }
    
    // 如果交易对是BNB，则通过BNB计算USDT价值
    if (pairSymbol === 'BNB' && priceInfo.price && priceInfo.bnbUsdtRate) {
      const valueInBnb = tokenAmount * parseFloat(priceInfo.price);
      return valueInBnb * parseFloat(priceInfo.bnbUsdtRate);
    }
    
    return 0;
  };

  // 添加一个异步函数来获取更准确的代币USDT价值（可用于重要计算）
  const getTokenUsdtValueAsync = async (tokenAmount) => {
    if (!tokenAddress || !tokenAmount || isNaN(tokenAmount) || tokenAmount <= 0) {
      return 0;
    }
    
    try {
      // 基于交易对不同，选择最佳路径计算USDT价值
      if (selectedPair.symbol === 'USDT') {
        // 如果交易对就是USDT，直接使用价格
        return tokenAmount * parseFloat(priceInfo.price);
      } else if (selectedPair.symbol === 'FIST') {
        // 如果交易对是FIST，则通过FIST-USDT路径计算
        const tokenToFistValue = tokenAmount * parseFloat(priceInfo.price);
        return await getFistUsdtValue(rpcProvider, tokenToFistValue);
      } else {
        // 对于其他交易对，尝试通过FIST计算
        return await getTokenUsdtValueViaFist(
          rpcProvider,
          tokenAddress,
          tokenAmount,
          tokenInfo.decimals
        );
      }
    } catch (err) {
      console.error("获取代币USDT价值失败:", err);
      // 失败时回退到同步计算方法
      return calculateTokenUsdtValue(tokenAmount, tokenInfo.symbol, selectedPair.symbol, priceInfo);
    }
  };

  // 修改 fetchWalletsBalances 函数，只使用简单的属性结构
  const fetchWalletsBalances = async (walletsToUpdate) => {
    if (!rpcProvider || !tokenAddress) {
      showMessage('请先设置RPC和代币地址', 'error');
      return;
    }

    try {
      const updatedWallets = await Promise.all(walletsToUpdate.map(async (wallet) => {
        try {
          // 获取BNB余额
          const bnbBalance = await rpcProvider.getBalance(wallet.address);
          const formattedBnbBalance = ethers.formatUnits(bnbBalance, DEFAULT_TOKENS.BNB.decimals);

          // 获取目标代币余额
          let formattedTokenBalance = '0';
          try {
            const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, rpcProvider);
            const tokenBalance = await tokenContract.balanceOf(wallet.address);
            formattedTokenBalance = ethers.formatUnits(tokenBalance, tokenInfo.decimals);
          } catch (err) {
            console.error(`获取${tokenInfo.symbol}余额失败:`, err);
          }

          // 获取FIST余额
          let formattedFistBalance = '0';
          try {
            const fistContract = new ethers.Contract(DEFAULT_TOKENS.FIST.address, ERC20_ABI, rpcProvider);
            const fistBalance = await fistContract.balanceOf(wallet.address);
            formattedFistBalance = ethers.formatUnits(fistBalance, DEFAULT_TOKENS.FIST.decimals);
          } catch (err) {
            console.error('获取FIST余额失败:', err);
          }

          // 计算USDT价值
          let tokenUsdtValue = '0';
          try {
            if (parseFloat(formattedTokenBalance) > 0) {
              const usdtValue = await getTokenUsdtValueAsync(formattedTokenBalance);
              tokenUsdtValue = formatPrice(usdtValue);
            }
          } catch (err) {
            console.error('计算代币USDT价值失败:', err);
          }

          // 返回简单的数据结构
          return {
            ...wallet,
            bnbBalance: formattedBnbBalance,
            tokenBalance: formattedTokenBalance,
            fistBalance: formattedFistBalance,
            tokenUsdtValue: tokenUsdtValue
          };
        } catch (walletError) {
          console.error(`获取钱包 ${wallet.address} 余额失败:`, walletError);
          // 返回带有默认余额的钱包对象
          return {
            ...wallet,
            bnbBalance: '0',
            tokenBalance: '0',
            fistBalance: '0',
            tokenUsdtValue: '0'
          };
        }
      }));

      // 更新钱包列表
      setWallets(prev => {
        const walletMap = new Map(prev.map(w => [w.address, w]));
        updatedWallets.forEach(w => walletMap.set(w.address, w));
        return Array.from(walletMap.values());
      });

      // 计算总余额
      if (updatedWallets.length > 0) {
        calculateTotalBalances(updatedWallets);
      }

      showMessage('钱包余额更新完成', 'success');
    } catch (error) {
      console.error('获取钱包余额失败:', error);
      handleError(error, '获取钱包余额');
    }
  };

  // 获取BNB-USDT价格
  const getBnbUsdtPrice = async (provider) => {
    try {
      const router = new ethers.Contract(PANCAKE_ROUTER_V2, PANCAKE_ROUTER_ABI, provider);
      const path = [DEFAULT_TOKENS.BNB.address, DEFAULT_TOKENS.USDT.address];
      const amounts = await router.getAmountsOut(ethers.parseEther('1'), path);
      return ethers.formatUnits(amounts[1], DEFAULT_TOKENS.USDT.decimals);
    } catch (err) {
      console.error("获取BNB-USDT价格失败:", err);
      return '0';
    }
  };

  // 获取FIST-USDT价格
  const getFistUsdtPrice = async (provider) => {
    try {
      return await getFistUsdtValue(provider);
    } catch (err) {
      console.error("获取FIST-USDT价格失败:", err);
      return '0';
    }
  };

  // 修改 fetchPriceAndLiquidity 函数
  const fetchPriceAndLiquidity = async () => {
    if (!rpcProvider || !tokenAddress) return null;
  
    try {
      setLoading(true);
      
      // 获取BNB/USDT价格
      const bnbUsdtPrice = await getBnbUsdtPrice(rpcProvider);
      
      // 获取FIST/USDT价格
      const fistUsdtPrice = await getFistUsdtPrice(rpcProvider);
      
      // 更新全局变量
      currentFistUsdtRate = fistUsdtPrice;
      
      // 检查代币是否有选择的交易对
      const factory = new ethers.Contract(PANCAKE_FACTORY_V2, PANCAKE_FACTORY_ABI, rpcProvider);
      const pairAddress = await factory.getPair(tokenAddress, selectedPair.address);
      
      if (pairAddress === ethers.ZeroAddress) {
        setPriceInfo({
          price: '0',
          usdtPrice: '0',
          liquidity: '0',
          pairToken: selectedPair.symbol,
          status: 'No Liquidity',
          bnbUsdtRate: bnbUsdtPrice,
          fistUsdtRate: fistUsdtPrice,
          source: 'PancakeSwap',
          timestamp: new Date().toLocaleString()
        });
        return;
      }
      
      // 获取流动性信息和价格
      const pairContract = new ethers.Contract(pairAddress, [
        "function getReserves() external view returns (uint112 reserve0, uint112 reserve1, uint32 blockTimestampLast)",
        "function token0() external view returns (address)"
      ], rpcProvider);
      
      const reserves = await pairContract.getReserves();
      const token0 = await pairContract.token0();
      let liquidity;
      let tokenPrice;
      
      // 确定哪个是交易对代币的储备
      if (token0.toLowerCase() === tokenAddress.toLowerCase()) {
        // 如果代币是token0，那么reserve1就是交易对的流动性
        liquidity = ethers.formatUnits(reserves.reserve1, selectedPair.decimals);
        
        // 价格计算：reserve1 / reserve0 (考虑不同的小数位)
        // 1个代币值多少个交易对代币
        const adjustedReserve0 = ethers.parseUnits(ethers.formatUnits(reserves.reserve0, tokenInfo.decimals), 18);
        const adjustedReserve1 = ethers.parseUnits(ethers.formatUnits(reserves.reserve1, selectedPair.decimals), 18);
        tokenPrice = ethers.formatUnits(
          (adjustedReserve1 * ethers.parseUnits('1', 18)) / adjustedReserve0,
          18
        );
      } else {
        // 如果代币是token1，那么reserve0就是交易对的流动性
        liquidity = ethers.formatUnits(reserves.reserve0, selectedPair.decimals);
        
        // 价格计算：reserve0 / reserve1 (考虑不同的小数位)
        // 1个代币值多少个交易对代币
        const adjustedReserve0 = ethers.parseUnits(ethers.formatUnits(reserves.reserve0, selectedPair.decimals), 18);
        const adjustedReserve1 = ethers.parseUnits(ethers.formatUnits(reserves.reserve1, tokenInfo.decimals), 18);
        tokenPrice = ethers.formatUnits(
          (adjustedReserve0 * ethers.parseUnits('1', 18)) / adjustedReserve1,
          18
        );
      }
      
      // 计算USDT价格
      let usdtPrice = '0';
      if (selectedPair.symbol === 'BNB') {
        usdtPrice = (parseFloat(tokenPrice) * parseFloat(bnbUsdtPrice)).toString();
      } else if (selectedPair.symbol === 'USDT') {
        usdtPrice = tokenPrice;
      } else if (selectedPair.symbol === 'BUSD') {
        // 获取BUSD/USDT价格
        const router = new ethers.Contract(PANCAKE_ROUTER_V2, PANCAKE_ROUTER_ABI, rpcProvider);
        const busdUsdtPath = [DEFAULT_TOKENS.BUSD.address, DEFAULT_TOKENS.USDT.address];
        const busdUsdtAmounts = await router.getAmountsOut(ethers.parseUnits('1', DEFAULT_TOKENS.BUSD.decimals), busdUsdtPath);
        const busdUsdtPrice = ethers.formatUnits(busdUsdtAmounts[1], DEFAULT_TOKENS.USDT.decimals);
        usdtPrice = (parseFloat(tokenPrice) * parseFloat(busdUsdtPrice)).toString();
      } else if (selectedPair.symbol === 'FIST') {
        // 如果选择FIST作为交易对，使用FIST-USDT价格计算USDT价值
        usdtPrice = (parseFloat(tokenPrice) * parseFloat(fistUsdtPrice)).toString();
      }
      
      const newPriceInfo = {
        price: formatPrice(tokenPrice),
        usdtPrice: formatPrice(usdtPrice),
        liquidity: liquidity,
        pairToken: selectedPair.symbol,
        status: 'Active',
        bnbUsdtRate: bnbUsdtPrice,
        fistUsdtRate: fistUsdtPrice,
        source: 'PancakeSwap',
        timestamp: new Date().toLocaleString()
      };
      
      setPriceInfo(newPriceInfo);
      setError(null);
      return newPriceInfo;
    } catch (err) {
      console.error("获取价格和流动性失败:", err);
      setPriceInfo(prev => ({
        ...prev,
        status: 'Error',
        timestamp: new Date().toLocaleString()
      }));
      setError(`获取价格失败: ${err.message}`);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // 添加钱包管理工具函数
  const validateWallet = (wallet) => {
    if (!wallet.address || !wallet.privateKey) {
      return false;
    }
    try {
      // 验证地址格式
      if (!ethers.isAddress(wallet.address)) {
        return false;
      }
      // 验证私钥格式
      if (!ethers.isHexString(wallet.privateKey, 32)) {
        return false;
      }
      return true;
    } catch (err) {
      return false;
    }
  };

  // 修改 importPrivateKeys 函数，使其使用 rpcProvider 而不是 provider 来获取余额
  const importPrivateKeys = async () => {
    if (!privateKeys.trim()) {
      setError('请输入有效的私钥');
      return;
    }

    try {
      setImportLoading(true);
      setError(null);
      
      const keys = privateKeys.split('\n').map(key => key.trim()).filter(key => key);
      if (keys.length === 0) {
        throw new Error('请输入有效的私钥');
      }
      
      const newWallets = [];
      const invalidKeys = [];
      
      for (const key of keys) {
        try {
          // 不需要连接到provider，只需创建钱包实例
          const wallet = new ethers.Wallet(key);
          const walletData = {
            address: wallet.address,
            privateKey: wallet.privateKey,
            isSelected: false,
            bnbBalance: '0',
            tokenBalance: '0',
            fistBalance: '0'
          };
          
          if (!wallets.some(w => w.address.toLowerCase() === wallet.address.toLowerCase())) {
            newWallets.push(walletData);
          }
        } catch (err) {
          invalidKeys.push(key);
        }
      }
      
      if (newWallets.length === 0 && invalidKeys.length > 0) {
        throw new Error('没有有效的私钥');
      }
      
      // 更新钱包列表
      const updatedWallets = [...wallets, ...newWallets];
      setWallets(updatedWallets);
      
      // 清空私钥输入框
      setPrivateKeys('');
      
      // 如果有代币地址和RPC提供者，立即获取余额
      if (tokenAddress && rpcProvider) {
        await fetchWalletsBalances(newWallets);
      } else if (rpcProvider) {
        // 如果没有代币地址，只获取BNB余额
        try {
          const walletsWithBnb = await Promise.all(
            newWallets.map(async wallet => {
              const bnbBalance = await rpcProvider.getBalance(wallet.address);
              return {
                ...wallet,
                bnbBalance: ethers.formatEther(bnbBalance)
              };
            })
          );
          
          // 更新钱包列表，包含BNB余额
          setWallets(prev => {
            const existingWallets = prev.filter(w => 
              !newWallets.some(nw => nw.address.toLowerCase() === w.address.toLowerCase())
            );
            return [...existingWallets, ...walletsWithBnb];
          });
          
          // 重新计算总余额
          calculateTotalBalances([...wallets.filter(w => 
            !newWallets.some(nw => nw.address.toLowerCase() === w.address.toLowerCase())
          ), ...walletsWithBnb]);
        } catch (err) {
          console.error("获取BNB余额失败:", err);
        }
      }
      
      if (invalidKeys.length > 0) {
        setError(`成功导入 ${newWallets.length} 个钱包，${invalidKeys.length} 个私钥无效`);
      } else {
        setError(`成功导入 ${newWallets.length} 个钱包`);
      }
    } catch (err) {
      handleError(err, '导入私钥');
    } finally {
      setImportLoading(false);
    }
  };

  // 修改 checkWalletBalances 函数
  const checkWalletBalances = async () => {
    try {
      setUpdateBalanceLoading(true);
      setError(null);
      
      // 只获取 BNB 余额
      const updatedWallets = await Promise.all(
        wallets.map(async (wallet) => {
          const bnbBalance = await rpcProvider.getBalance(wallet.address);
          
          return {
            ...wallet,
            bnbBalance: ethers.formatEther(bnbBalance),
            tokenBalance: tokenAddress ? wallet.tokenBalance : '0' // 如果没有代币地址,保持代币余额为0
          };
        })
      );
      
      setWallets(updatedWallets);
      calculateTotalBalances(updatedWallets);
      setError('余额更新完成');
    } catch (err) {
      console.error("更新余额失败:", err);
      setError(`更新余额失败: ${err.message}`);
    } finally {
      setUpdateBalanceLoading(false);
    }
  };

  // 修改 checkAndRebalanceWallets 函数，删除管理员权限检查
  const checkAndRebalanceWallets = async () => {
    if (wallets.length < 2) {
      setError("需要至少两个钱包");
      return;
    }
    
    try {
      setLoading(true);
      
      const MIN_BNB_BALANCE = 0.005;
      const TARGET_BNB_BALANCE = 0.01;
      
      const sufficientWallets = wallets.filter(w => parseFloat(w.bnbBalance) > MIN_BNB_BALANCE * 2);
      const insufficientWallets = wallets.filter(w => parseFloat(w.bnbBalance) < MIN_BNB_BALANCE);
      
      if (insufficientWallets.length === 0) {
        setError("所有钱包余额均足够");
        setLoading(false);
        return;
      }
      
      if (sufficientWallets.length === 0) {
        setError("没有余额足够的钱包可用于转账");
        setLoading(false);
        return;
      }
      
      setPendingAction({
        type: 'rebalance',
        sufficientWallets,
        insufficientWallets,
        MIN_BNB_BALANCE,
        TARGET_BNB_BALANCE
      });
      setShowConfirmDialog(true);
      setLoading(false);
    } catch (err) {
      console.error("余额重新分配失败:", err);
      setError(`余额重新分配失败: ${err.message}`);
      setLoading(false);
    }
  };

  // 添加错误处理工具函数
  const handleError = (error, context) => {
    console.error(`[${context}] 错误:`, error);
    const errorMessage = error.message || '未知错误';
    setError(`${context}: ${errorMessage}`);
    setTxHistory(prev => [{
      wallet: activeTx?.wallet || '系统',
      type: context,
      error: errorMessage,
      timestamp: new Date().toLocaleString(),
      status: '失败'
    }, ...prev]);
  };

  // 添加日志记录函数
  const generateTransactionId = (wallet, txType, timestamp) => {
    return `${wallet}_${txType}_${timestamp}`;
  };

  // 修改日志记录函数，添加去重功能
  const logTransaction = (txRecord) => {
    // 生成唯一交易ID
    const txId = generateTransactionId(
      txRecord.wallet,
      txRecord.type,
      txRecord.timestamp
    );
    
    // 确保receivedAmount正确格式化显示
    const formattedReceived = txRecord.receivedAmount ? 
      `${typeof txRecord.receivedAmount === 'string' ? 
        (parseFloat(txRecord.receivedAmount) === 0 ? '0.0' : txRecord.receivedAmount) : 
        txRecord.receivedAmount} ${txRecord.receivedSymbol}` : 
      '0.0';
    
    console.log(`[交易记录] ${txRecord.type}:`, {
      wallet: txRecord.wallet,
      amount: txRecord.spentAmount,
      symbol: txRecord.spentSymbol,
      received: formattedReceived,
      targetToken: txRecord.receivedSymbol || '未知代币',
      status: txRecord.status,
      timestamp: txRecord.timestamp,
      txId: txId
    });
    
    // 改进的去重逻辑 - 通过钱包地址和类型识别短时间内的相同交易
    const now = new Date();
    const TIME_WINDOW_MS = 10000; // 10秒内的相同类型交易被视为同一交易
    
    // 首先检查是否有相同的txHash（最精确的匹配）
    const exactMatchIndex = txRecord.txHash ? 
      txHistory.findIndex(tx => 
        tx.txHash === txRecord.txHash && 
        tx.type === txRecord.type && 
        tx.wallet === txRecord.wallet
      ) : -1;
    
    if (exactMatchIndex !== -1) {
      // 找到精确匹配，更新而不是添加
      const updatedHistory = [...txHistory];
      updatedHistory[exactMatchIndex] = {
        ...updatedHistory[exactMatchIndex],
        ...txRecord,
        // 保留原始时间戳，但记录更新时间
        updatedAt: now.toLocaleString()
      };
      setTxHistory(updatedHistory);
      return;
    }
    
    // 检查短时间窗口内的相同钱包和类型的交易
    const timeWindowMatchIndex = txHistory.findIndex(tx => {
      if (tx.wallet !== txRecord.wallet || tx.type !== txRecord.type) {
        return false;
      }
      
      // 计算时间差（毫秒）
      const txTime = new Date(tx.timestamp);
      const timeDiff = Math.abs(now - txTime);
      
      return timeDiff < TIME_WINDOW_MS;
    });
    
    if (timeWindowMatchIndex !== -1) {
      // 找到时间窗口内的匹配，更新而不是添加
      const updatedHistory = [...txHistory];
      // 合并记录，保留最重要的信息
      updatedHistory[timeWindowMatchIndex] = {
        ...updatedHistory[timeWindowMatchIndex],
        ...txRecord,
        // 如果新记录有交易哈希但旧记录没有，使用新的
        txHash: txRecord.txHash || updatedHistory[timeWindowMatchIndex].txHash,
        // 如果新记录有错误信息但旧记录没有，保留错误信息
        error: txRecord.error || updatedHistory[timeWindowMatchIndex].error,
        // 更新状态，优先使用最终状态（失败或已完成）
        status: ['失败', '已完成'].includes(txRecord.status) ? 
               txRecord.status : 
               updatedHistory[timeWindowMatchIndex].status,
        // 保留原始时间戳，但记录更新时间
        updatedAt: now.toLocaleString()
      };
      setTxHistory(updatedHistory);
    } else {
      // 添加新交易记录
      setTxHistory(prev => [{
        ...txRecord,
        createdAt: now.toLocaleString()
      }, ...prev]);
    }
  };

  // 更新交易状态
  const updateTransactionStatus = (status) => {
    setTransactionStatus(prev => ({
      ...prev,
      ...status
    }));
  };

  // 重置交易状态
  const resetTransactionStatus = () => {
    setTransactionStatus({
      isProcessing: false,
      currentOperation: '',
      progress: 0,
      total: 0,
      successCount: 0,
      failCount: 0,
      lastError: null
    });
  };

  // 计算预期买入量 - 支持不同的买入代币
  const calculateExpectedBuyAmount = async (amountWei, buyTokenType) => {
    try {
      console.log(`---------- 开始计算预期代币获得量 ----------`);
      console.log(`输入: ${ethers.formatUnits(amountWei, DEFAULT_TOKENS[buyTokenType].decimals)} ${buyTokenType}`);
      console.log(`目标: ${tokenInfo.symbol}`);
      
      const router = new ethers.Contract(PANCAKE_ROUTER_V2, PANCAKE_ROUTER_ABI, rpcProvider);
      // 根据买入代币选择不同的路径
      const buyTokenAddress = DEFAULT_TOKENS[buyTokenType].address;
      let path = [buyTokenAddress, tokenAddress];
      let amounts;
      
      // 智能路径选择
      const tryPath = async (pathDescription, pathArray) => {
        try {
          console.log(`尝试${pathDescription}: [${pathArray.map(addr => {
            if (addr === buyTokenAddress) return buyTokenType;
            if (addr === tokenAddress) return tokenInfo.symbol;
            if (addr === DEFAULT_TOKENS.USDT.address) return 'USDT';
            if (addr === DEFAULT_TOKENS.BNB.address) return 'BNB';
            return addr.substring(0, 6) + '...' + addr.substring(addr.length - 4);
          }).join(' -> ')}]`);
          
          const pathAmounts = await router.getAmountsOut(amountWei, pathArray);
          console.log(`${pathDescription}预期获得: ${ethers.formatUnits(pathAmounts[pathAmounts.length - 1], tokenInfo.decimals)} ${tokenInfo.symbol}`);
          return {
            success: true,
            amounts: pathAmounts,
            path: pathArray
          };
        } catch (err) {
          console.warn(`${pathDescription}预估失败: ${err.message}`);
          return { success: false, error: err };
        }
      };
      
      // 尝试直接路径
      const directResult = await tryPath('直接路径', path);
      
      if (directResult.success) {
        // 直接路径成功
        amounts = directResult.amounts;
        path = directResult.path;
      } else if (buyTokenType !== 'BNB' && buyTokenType !== 'USDT') {
        // 尝试通过USDT中转
        const usdtPath = [buyTokenAddress, DEFAULT_TOKENS.USDT.address, tokenAddress];
        const usdtResult = await tryPath('USDT中转路径', usdtPath);
        
        if (usdtResult.success) {
          amounts = usdtResult.amounts;
          path = usdtResult.path;
        } else {
          // 尝试通过BNB中转
          const bnbPath = [buyTokenAddress, DEFAULT_TOKENS.BNB.address, tokenAddress];
          const bnbResult = await tryPath('BNB中转路径', bnbPath);
          
          if (bnbResult.success) {
            amounts = bnbResult.amounts;
            path = bnbResult.path;
          } else {
            // 所有路径都失败
            throw new Error('所有路径估算均失败');
          }
        }
      } else {
        // 如果是BNB或USDT，只尝试另一个作为中转
        const intermediateCoin = buyTokenType === 'BNB' ? 'USDT' : 'BNB';
        const alternatePath = [
          buyTokenAddress, 
          DEFAULT_TOKENS[intermediateCoin].address, 
          tokenAddress
        ];
        
        const alternateResult = await tryPath(`${intermediateCoin}中转路径`, alternatePath);
        
        if (alternateResult.success) {
          amounts = alternateResult.amounts;
          path = alternateResult.path;
        } else {
          throw new Error('无法找到有效交易路径');
        }
      }
      
      // 保存最终使用的路径到会话存储，供后续交易使用
      sessionStorage.setItem('lastBuyPathForToken_' + tokenAddress, JSON.stringify({
        buyToken: buyTokenType,
        path: path,
        timestamp: Date.now()
      }));
      
      // 返回预期获得的代币数量（路径的最后一个元素）
      const expectedAmount = amounts[amounts.length - 1];
      console.log(`---------- 预期计算完成 ----------`);
      console.log(`最终选择路径: [${path.map(addr => {
        if (addr === buyTokenAddress) return buyTokenType;
        if (addr === tokenAddress) return tokenInfo.symbol;
        if (addr === DEFAULT_TOKENS.USDT.address) return 'USDT';
        if (addr === DEFAULT_TOKENS.BNB.address) return 'BNB';
        return addr.substring(0, 6) + '...' + addr.substring(addr.length - 4);
      }).join(' -> ')}]`);
      console.log(`最终预期获得: ${ethers.formatUnits(expectedAmount, tokenInfo.decimals)} ${tokenInfo.symbol}`);
      
      return {
        expectedAmount,
        path
      };
    } catch (err) {
      console.error(`计算预期买入量失败 (${buyTokenType}):`, err);
      
      // 使用保守估计值
      let estimatedAmount;
      
      // 尝试使用市场价格进行预估
      if (priceInfo.price && parseFloat(priceInfo.price) > 0) {
        const inputAmount = parseFloat(ethers.formatUnits(amountWei, DEFAULT_TOKENS[buyTokenType].decimals));
        let convertRate = 1.0;
        
        if (buyTokenType === 'FIST') {
          convertRate = 1.0 / parseFloat(priceInfo.price);
        } else if (buyTokenType === 'BNB') {
          // 假设BNB价格在priceInfo中
          convertRate = parseFloat(priceInfo.bnbToTokenRate || '1.0');
        } else if (buyTokenType === 'USDT') {
          convertRate = 1.0 / parseFloat(priceInfo.usdtPrice || '1.0');
        }
        
        estimatedAmount = inputAmount * convertRate * 0.95; // 应用5%的保守折扣
        console.log(`使用市场价格估算: ${estimatedAmount} ${tokenInfo.symbol}`);
      } else {
        // 使用滑点作为保守估计
        const slippagePercent = parseFloat(buySlippage);
        estimatedAmount = parseFloat(ethers.formatUnits(amountWei, DEFAULT_TOKENS[buyTokenType].decimals)) * (1 - slippagePercent / 100);
        console.log(`使用滑点估算: ${estimatedAmount} ${tokenInfo.symbol}`);
      }
      
      // 使用默认直接路径
      const defaultPath = [DEFAULT_TOKENS[buyTokenType].address, tokenAddress];
      
      return {
        expectedAmount: ethers.parseUnits(estimatedAmount.toString(), tokenInfo.decimals),
        path: defaultPath
      };
    }
  };

  // 计算预期卖出量 - 支持不同的卖出代币
  const calculateExpectedSellAmount = async (amountWei, sellTokenType) => {
    try {
      console.log(`---------- 开始计算预期卖出获得量 ----------`);
      console.log(`卖出代币: ${tokenInfo.symbol}`);
      console.log(`获得代币: ${sellTokenType}`);
      console.log(`卖出数量: ${ethers.formatUnits(amountWei, tokenInfo.decimals)} ${tokenInfo.symbol}`);
      
      const router = new ethers.Contract(PANCAKE_ROUTER_V2, PANCAKE_ROUTER_ABI, rpcProvider);
      
      // 根据卖出代币选择不同的路径
      const sellTokenAddress = DEFAULT_TOKENS[sellTokenType].address;
      let path = [tokenAddress, sellTokenAddress];
      let amounts;
      
      // 对FIST进行特殊处理，尝试两种路径
      if (sellTokenType === 'FIST') {
        try {
          // 先尝试直接路径
          console.log(`尝试直接路径: [${tokenInfo.symbol} -> FIST]`);
          amounts = await router.getAmountsOut(amountWei, path);
          console.log(`直接路径预期获得: ${ethers.formatUnits(amounts[1], DEFAULT_TOKENS.FIST.decimals)} FIST`);
        } catch (directPathError) {
          console.warn(`直接路径预估失败: ${directPathError.message}`);
          
          // 如果直接路径失败，尝试通过USDT中转
          console.log(`尝试间接路径: [${tokenInfo.symbol} -> USDT -> FIST]`);
          const indirectPath = [tokenAddress, DEFAULT_TOKENS.USDT.address, DEFAULT_TOKENS.FIST.address];
          
          try {
            amounts = await router.getAmountsOut(amountWei, indirectPath);
            path = indirectPath; // 更新路径为间接路径
            console.log(`间接路径预期获得: ${ethers.formatUnits(amounts[2], DEFAULT_TOKENS.FIST.decimals)} FIST`);
          } catch (indirectPathError) {
            console.error(`间接路径预估也失败: ${indirectPathError.message}`);
            throw new Error('无法估算FIST获得量，两种路径均失败');
          }
        }
      } else {
        // 其他代币使用常规路径
        console.log(`使用常规路径: [${tokenInfo.symbol} -> ${sellTokenType}]`);
        amounts = await router.getAmountsOut(amountWei, path);
        console.log(`预期获得: ${ethers.formatUnits(amounts[amounts.length - 1], DEFAULT_TOKENS[sellTokenType].decimals)} ${sellTokenType}`);
      }
      
      // 返回预期获得的代币数量（路径的最后一个元素）
      const expectedAmount = amounts[amounts.length - 1];
      console.log(`---------- 预期卖出计算完成 ----------`);
      return expectedAmount;
    } catch (err) {
      console.error(`计算预期卖出量失败 (${sellTokenType}):`, err);
      
      // 尝试使用当前价格信息进行预估
      if (priceInfo.price && parseFloat(priceInfo.price) > 0) {
        // 使用当前市场价格计算
        const tokenAmount = parseFloat(ethers.formatUnits(amountWei, tokenInfo.decimals));
        let estimatedAmount;
        
        if (sellTokenType === 'FIST') {
          estimatedAmount = tokenAmount * parseFloat(priceInfo.price);
          console.log(`使用市场价格估算FIST: ${estimatedAmount} FIST`);
        } else if (sellTokenType === 'BNB') {
          estimatedAmount = tokenAmount * parseFloat(priceInfo.price);
          console.log(`使用市场价格估算BNB: ${estimatedAmount} BNB`);
        } else if (sellTokenType === 'USDT') {
          estimatedAmount = tokenAmount * parseFloat(priceInfo.usdtPrice);
          console.log(`使用市场价格估算USDT: ${estimatedAmount} USDT`);
        }
        
        // 为了安全，对于FIST代币，考虑可能的额外费用
        if (sellTokenType === 'FIST') {
          // 假设FIST可能有5%的转账费，预先减少预期获得量
          estimatedAmount = estimatedAmount * 0.95;
          console.log(`考虑可能的FIST转账费后估算: ${estimatedAmount} FIST`);
        }
        
        return ethers.parseUnits(estimatedAmount.toFixed(6).toString(), DEFAULT_TOKENS[sellTokenType].decimals);
      }
      
      // 如果无法使用市场价格，返回一个保守的估计值
      const slippageAmount = parseFloat(ethers.formatUnits(amountWei, tokenInfo.decimals)) * 
                            parseFloat(priceInfo.price || '0.00001') * 
                            (1 - parseFloat(sellSlippage) / 100);
      
      console.log(`使用保守估算: ${slippageAmount} ${sellTokenType}`);
      return ethers.parseUnits(slippageAmount.toString(), DEFAULT_TOKENS[sellTokenType].decimals);
    }
  };

  // 执行买入交易 - 支持不同的买入代币
  const processBuyTransaction = async (wallet, amount, currentIndex, totalCount, isVolumeTrade = false, customSlippage = null, buyTokenType = 'FIST') => {
    // 检查是否暂停交易
    if (transactionsPaused) {
      console.log(`交易已暂停，跳过买入交易 ${wallet.address}`);
      return false;
    }

    try {
      // 严格使用用户选择的代币
      const currentBuyToken = buyTokenType;
      console.log(`使用${currentBuyToken}购买代币，精度: ${DEFAULT_TOKENS[currentBuyToken].decimals}`);

      updateTransactionStatus({
        isProcessing: true,
        currentOperation: `买入交易 (使用${currentBuyToken})`,
        progress: currentIndex,
        total: totalCount
      });
      
      // 根据代币精度格式化金额 - 增加有效性检查
      if (amount === undefined || amount === null || amount === '') {
        throw new Error(`无效的购买金额: ${amount}`);
      }
      
      // 确保amount是有效数字
      const parsedAmount = parseFloat(amount);
      if (isNaN(parsedAmount) || parsedAmount <= 0) {
        throw new Error(`购买金额必须是大于0的有效数字，当前值: ${amount}`);
      }
      
      // 格式化为指定精度的字符串
      const formattedAmount = parsedAmount.toFixed(DEFAULT_TOKENS[currentBuyToken].decimals);
      console.log(`买入金额格式化: ${amount} -> ${formattedAmount} (${currentBuyToken} 精度: ${DEFAULT_TOKENS[currentBuyToken].decimals})`);
      
      // 转换为区块链上使用的大整数
      const buyAmount = ethers.parseUnits(formattedAmount, DEFAULT_TOKENS[currentBuyToken].decimals);
      
      // 使用rpcProvider检查余额
      let buyTokenBalance;
      if (currentBuyToken === 'BNB') {
        buyTokenBalance = await rpcProvider.getBalance(wallet.address);
      } else {
        const buyTokenContract = new ethers.Contract(DEFAULT_TOKENS[currentBuyToken].address, ERC20_ABI, rpcProvider);
        buyTokenBalance = await buyTokenContract.balanceOf(wallet.address);
      }
      
      if (buyTokenBalance < buyAmount) {
        throw new Error(`钱包 ${wallet.address} ${currentBuyToken}余额不足，需要 ${ethers.formatUnits(buyAmount, DEFAULT_TOKENS[currentBuyToken].decimals)} ${currentBuyToken}，但只有 ${ethers.formatUnits(buyTokenBalance, DEFAULT_TOKENS[currentBuyToken].decimals)} ${currentBuyToken}`);
      }
      
      // 获取预期收到的代币数量
      const buyResult = await calculateExpectedBuyAmount(buyAmount, currentBuyToken);
      const expectedTokens = buyResult.expectedAmount;
      const transactionPath = buyResult.path;
      const slippagePercent = parseFloat(customSlippage || (isVolumeTrade ? volumeBuySlippage : buySlippage));
      
      console.log(`---------- 滑点计算详情 ----------`);
      console.log(`自定义滑点: ${customSlippage}`);
      console.log(`常规买入滑点: ${buySlippage}`);
      console.log(`做市买入滑点: ${volumeBuySlippage}`);
      console.log(`是否做市交易: ${isVolumeTrade}`);
      console.log(`最终使用滑点: ${slippagePercent}%`);
      
      // 详细展示滑点计算过程
      console.log(`滑点计算公式: 最低接收 = 预期数量 * (100 - 滑点百分比) / 100`);
      console.log(`预期代币数量: ${expectedTokens.toString()}`);
      console.log(`计算步骤: ${expectedTokens} * ${Math.floor((100 - slippagePercent) * 100)} / 10000`);
      
      const minTokens = expectedTokens * BigInt(Math.floor((100 - slippagePercent) * 100)) / 10000n;
      
      console.log(`最终最低接收数量: ${minTokens.toString()}`);
      console.log(`---------- 滑点计算结束 ----------`);
      
      console.log(`---------- 交易参数详情 ----------`);
      console.log(`买入代币: ${currentBuyToken}`);
      console.log(`买入金额: ${ethers.formatUnits(buyAmount, DEFAULT_TOKENS[currentBuyToken].decimals)} ${currentBuyToken}`);
      console.log(`预期获得: ${ethers.formatUnits(expectedTokens, tokenInfo.decimals)} ${tokenInfo.symbol}`);
      console.log(`应用滑点: ${slippagePercent}%`);
      console.log(`最低接收: ${ethers.formatUnits(minTokens, tokenInfo.decimals)} ${tokenInfo.symbol}`);
      console.log(`交易路径: [${transactionPath.map(addr => {
        if (addr === DEFAULT_TOKENS[currentBuyToken].address) return currentBuyToken;
        if (addr === tokenAddress) return tokenInfo.symbol;
        if (addr === DEFAULT_TOKENS.USDT.address) return 'USDT';
        if (addr === DEFAULT_TOKENS.BNB.address) return 'BNB';
        return addr.substring(0, 6) + '...' + addr.substring(addr.length - 4);
      }).join(' -> ')}]`);
      console.log(`钱包地址: ${wallet.address}`);
      console.log(`---------- 交易参数结束 ----------`);
      
      // 记录交易前余额
      const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, rpcProvider);
      const beforeTokenBalance = await tokenContract.balanceOf(wallet.address);
      
      // 设置当前活动交易
      setActiveTx({
        wallet: wallet.address,
        type: `买入 (使用${currentBuyToken})`,
        amount: `${formattedAmount} ${currentBuyToken}`,
        status: '检查授权中',
        index: currentIndex,
        total: totalCount,
        isVolumeTrade
      });
      
      // 使用私钥和rpcProvider创建签名者
      const walletSigner = new ethers.Wallet(wallet.privateKey, rpcProvider);
      const router = new ethers.Contract(PANCAKE_ROUTER_V2, PANCAKE_ROUTER_ABI, walletSigner);
      
      let tx;
      
      // 获取当前gas价格
      const gasPrice = await rpcProvider.getFeeData();
      const txParams = {
        gasLimit: 500000,
        gasPrice: useCustomGas && customGasPrice ? 
          ethers.parseUnits(customGasPrice, 'gwei') : 
          gasPrice.gasPrice * 5n
      };
      
      // 检查BNB余额是否足够支付Gas费用
      const bnbBalance = await rpcProvider.getBalance(wallet.address);
      // 估算所需的最小BNB余额 = gasPrice * gasLimit
      const estimatedGasCost = txParams.gasPrice * BigInt(txParams.gasLimit);
      // 为避免计算误差，增加20%的缓冲
      const requiredBnbBalance = estimatedGasCost * 120n / 100n;
      
      if (bnbBalance < requiredBnbBalance) {
        throw new Error(`钱包 ${wallet.address} BNB余额不足以支付Gas费用，需要约 ${ethers.formatEther(requiredBnbBalance)} BNB，但只有 ${ethers.formatEther(bnbBalance)} BNB`);
      }
      
      // 尝试执行交易的函数
      const executeTransaction = async (method, supportFeeTransfer = false, increasedGas = false) => {
        // 检查是否是已知的特殊代币，自动使用支持转账费的方法
        const isSpecialToken = SPECIAL_TOKENS[tokenInfo.symbol] === true;
        if (isSpecialToken && !supportFeeTransfer) {
          console.log(`检测到特殊代币 ${tokenInfo.symbol}，自动使用支持转账费的方法`);
          supportFeeTransfer = true;
          increasedGas = true;
        }
        
        let methodDesc = supportFeeTransfer ? 
          'swapExactTokensForTokensSupportingFeeOnTransferTokens' : 
          (method === 'ETH' ? 'swapExactETHForTokens' : 'swapExactTokensForTokens');
        
        console.log(`---------- 执行交易 [${methodDesc}] ----------`);
        console.log(`买入数量: ${ethers.formatUnits(buyAmount, DEFAULT_TOKENS[currentBuyToken].decimals)} ${currentBuyToken}`);
        console.log(`最低获得: ${ethers.formatUnits(minTokens, tokenInfo.decimals)} ${tokenInfo.symbol}`);
        console.log(`交易路径: [${transactionPath.map(a => {
            if (a === DEFAULT_TOKENS[currentBuyToken].address) return currentBuyToken;
            if (a === tokenAddress) return tokenInfo.symbol;
            if (a === DEFAULT_TOKENS.USDT.address) return 'USDT';
            if (a === DEFAULT_TOKENS.BNB.address) return 'BNB';
            return a.substring(0, 6) + '...' + a.substring(a.length - 4);
        }).join(' -> ')}]`);
        
        if (increasedGas) {
          txParams.gasLimit = 900000;
          console.log(`使用增加的Gas限制: ${txParams.gasLimit}`);
        }
        
        try {
          let transaction;
          
          // 根据方法类型执行不同的交易
          if (method === 'ETH') {
            // ETH/BNB交易
        txParams.value = buyAmount;
            
            if (supportFeeTransfer) {
              transaction = await router.swapExactETHForTokensSupportingFeeOnTransferTokens(
          minTokens,
                transactionPath,
          wallet.address,
          Math.floor(Date.now() / 1000) + 60 * 20,
          txParams
        );
            } else {
              transaction = await router.swapExactETHForTokens(
                minTokens,
                transactionPath,
                wallet.address,
                Math.floor(Date.now() / 1000) + 60 * 20,
                txParams
              );
            }
          } else if (supportFeeTransfer) {
            // 支持转账费的代币交易
            transaction = await router.swapExactTokensForTokensSupportingFeeOnTransferTokens(
              buyAmount,
              minTokens,
              transactionPath,
              wallet.address,
              Math.floor(Date.now() / 1000) + 60 * 20,
              txParams
            );
          } else {
            // 普通代币交易
            transaction = await router.swapExactTokensForTokens(
              buyAmount,
              minTokens,
              transactionPath,
              wallet.address,
              Math.floor(Date.now() / 1000) + 60 * 20,
              txParams
            );
          }
          
          console.log(`交易已提交, 哈希: ${transaction.hash}`);
          return transaction;
        } catch (txError) {
          console.error(`交易执行失败 (${methodDesc}):`, txError.message);
          
          // 如果是TRANSFER_FAILED错误且不是支持转账费的方法，自动添加到特殊代币列表中
          if (txError.message.includes('TRANSFER_FAILED') && !supportFeeTransfer) {
            console.log(`检测到TRANSFER_FAILED错误，将 ${tokenInfo.symbol} 添加到特殊代币列表`);
            SPECIAL_TOKENS[tokenInfo.symbol] = true;
          }
          
          throw txError;
        }
      };
      
      // 根据不同代币尝试执行交易
      try {
        if (currentBuyToken === 'BNB') {
          setActiveTx(prev => ({
            ...prev,
            status: '执行买入交易 (使用BNB)'
          }));
          
          tx = await executeTransaction('ETH');
        } else {
          // 所有ERC20代币先需要检查授权
          const buyTokenContract = new ethers.Contract(DEFAULT_TOKENS[currentBuyToken].address, ERC20_ABI, walletSigner);
        const allowance = await buyTokenContract.allowance(wallet.address, PANCAKE_ROUTER_V2);
        
        if (allowance < buyAmount) {
            setActiveTx(prev => ({
              ...prev,
              status: '正在授权代币'
            }));
            
          await handleTokenApproval(buyTokenContract, PANCAKE_ROUTER_V2, wallet.address, gasPrice);
            
            // 授权后再次检查授权额度
            const newAllowance = await buyTokenContract.allowance(wallet.address, PANCAKE_ROUTER_V2);
            if (newAllowance < buyAmount) {
              throw new Error(`${currentBuyToken}代币授权失败，请检查钱包状态`);
            }
        }
        
        setActiveTx(prev => ({
          ...prev,
            status: `执行买入交易 (使用${currentBuyToken})`
          }));
          
          try {
            // 先尝试常规交易
            tx = await executeTransaction('TOKEN');
          } catch (standardError) {
            // 判断是否是转账相关问题
            if (standardError.message.includes('TRANSFER_FAILED') || 
                standardError.message.includes('INSUFFICIENT_OUTPUT_AMOUNT') ||
                standardError.message.includes('execution reverted') ||
                standardError.message.includes('transfer amount exceeds balance') ||
                standardError.message.includes('price impact too high') ||
                standardError.message.includes('cannot estimate gas') ||
                standardError.message.includes('transaction failed')) {
              
              console.log(`常规交易失败: ${standardError.message}`);
              console.log(`尝试使用支持转账费的方法并增加Gas限制...`);
              
              // 增加错误处理信息
              setActiveTx(prev => ({
                ...prev,
                status: '常规交易失败，尝试备用方法'
              }));
              
              // 根据错误类型调整参数
              let adjustedGasLimit = true;
              let adjustedSlippage = false;
              
              // 如果是滑点不足错误，增加滑点
              if (standardError.message.includes('INSUFFICIENT_OUTPUT_AMOUNT') || 
                  standardError.message.includes('price impact too high')) {
                const newSlippage = Math.min(slippagePercent * 1.5, 49); // 最高49%滑点
                if (newSlippage > slippagePercent) {
                  slippagePercent = newSlippage;
                  minTokens = expectedTokens * BigInt(Math.floor((100 - slippagePercent) * 100)) / 10000n;
                  adjustedSlippage = true;
                  console.log(`增加滑点至 ${slippagePercent}%`);
                  console.log(`新的最低获得数量: ${ethers.formatUnits(minTokens, tokenInfo.decimals)} ${tokenInfo.symbol}`);
                }
              }
              
              try {
                // 使用支持转账费的方法和调整后的参数重试
                tx = await executeTransaction('TOKEN', true, adjustedGasLimit);
                console.log(`使用支持转账费方法交易成功，哈希: ${tx.hash}`);
                
                // 添加到特殊代币列表，以便下次自动使用正确方法
                if (!SPECIAL_TOKENS[tokenInfo.symbol]) {
                  console.log(`将 ${tokenInfo.symbol} 添加到特殊代币列表，下次将自动使用支持转账费的方法`);
                  SPECIAL_TOKENS[tokenInfo.symbol] = true;
                }
              } catch (feeError) {
                console.error(`支持转账费方法也失败: ${feeError.message}`);
                
                // 如果是滑点不足且没有调整过滑点，尝试再次增加滑点
                if ((feeError.message.includes('INSUFFICIENT_OUTPUT_AMOUNT') || 
                     feeError.message.includes('price impact too high')) && 
                    !adjustedSlippage) {
                  
                  console.log(`尝试使用最高滑点再次重试交易...`);
                  const maxSlippage = 49; // 最高滑点
                  const maxSlippageMinTokens = expectedTokens * BigInt(Math.floor((100 - maxSlippage) * 100)) / 10000n;
                  
                  try {
                    // 使用最高滑点重试
                    txParams.gasLimit = 1000000; // 最高Gas限制
        tx = await router.swapExactTokensForTokensSupportingFeeOnTransferTokens(
          buyAmount,
                      maxSlippageMinTokens,
                      transactionPath,
          wallet.address,
          Math.floor(Date.now() / 1000) + 60 * 20,
          txParams
        );
                    console.log(`使用最高滑点交易成功，哈希: ${tx.hash}`);
                  } catch (maxSlippageError) {
                    console.error(`使用最高滑点也失败，尝试备用路径...`);
                    
                    // 尝试备用路径
                    let backupPaths = [];
                    
                    if (transactionPath.length === 2) {
                      // 当前是直接路径，尝试加入中间代币
                      backupPaths = [
                        [transactionPath[0], DEFAULT_TOKENS.BNB.address, transactionPath[1]],  // 通过BNB
                        [transactionPath[0], DEFAULT_TOKENS.USDT.address, transactionPath[1]]  // 通过USDT
                      ];
                    } else {
                      // 当前是间接路径，尝试不同的中间代币
                      const interToken = transactionPath[1] === DEFAULT_TOKENS.BNB.address ? 
                        DEFAULT_TOKENS.USDT.address : DEFAULT_TOKENS.BNB.address;
                      backupPaths = [
                        [transactionPath[0], interToken, transactionPath[2]]
                      ];
                    }
                    
                    let pathSuccess = false;
                    // 依次尝试备用路径
                    for (let i = 0; i < backupPaths.length; i++) {
                      const path = backupPaths[i];
                      try {
                        const pathDesc = path.map(a => {
                          if (a === DEFAULT_TOKENS[currentBuyToken].address) return currentBuyToken;
                          if (a === tokenAddress) return tokenInfo.symbol;
                          if (a === DEFAULT_TOKENS.USDT.address) return 'USDT';
                          if (a === DEFAULT_TOKENS.BNB.address) return 'BNB';
                          return a.substring(0, 6) + '...' + a.substring(a.length - 4);
                        }).join(' -> ');
                        
                        console.log(`尝试备用路径 ${i+1}: [${pathDesc}]`);
        setActiveTx(prev => ({
          ...prev,
                          status: `尝试备用路径 ${i+1}`
        }));
        
                        // 使用备用路径
        tx = await router.swapExactTokensForTokensSupportingFeeOnTransferTokens(
          buyAmount,
                          maxSlippageMinTokens,
                          path,
          wallet.address,
          Math.floor(Date.now() / 1000) + 60 * 20,
                          { ...txParams, gasLimit: 1000000 } // 使用最高gas限制
                        );
                        
                        console.log(`备用路径交易成功，哈希: ${tx.hash}`);
                        transactionPath = path; // 更新路径以便记录
                        pathSuccess = true;
                        break; // 成功后退出循环
                      } catch (pathError) {
                        console.error(`备用路径 ${i+1} 失败:`, pathError.message);
                        // 继续下一个路径
                      }
                    }
                    
                    if (!pathSuccess) {
                      throw maxSlippageError; // 所有备用路径都失败
                    }
                  }
                } else {
                  throw feeError; // 其他错误直接抛出
                }
              }
            } else {
              // 其他错误直接抛出
              throw standardError;
            }
          }
        }
      } catch (transactionError) {
        console.error(`交易最终失败:`, transactionError.message);
        
        // 检查错误是否与滑点或路径相关
        if (transactionError.message.includes('INSUFFICIENT_OUTPUT_AMOUNT') ||
            transactionError.message.includes('price impact too high') ||
            transactionError.message.includes('insufficient output amount') ||
            transactionError.message.includes('TRANSFER_FAILED')) {
          
          // 增加滑点并重试
          const newSlippage = Math.min(slippagePercent * 1.5, 49); // 最高49%滑点
          console.log(`尝试使用更高滑点 ${newSlippage}% 和支持转账费的方法重试交易...`);
          
          const newMinTokens = expectedTokens * BigInt(Math.floor((100 - newSlippage) * 100)) / 10000n;
          console.log(`新的最低接收数量: ${ethers.formatUnits(newMinTokens, tokenInfo.decimals)} ${tokenInfo.symbol}`);
          
          // 尝试使用新的滑点和支持费用转移的方法
          try {
            txParams.gasLimit = 900000; // 增加Gas限制
            
            if (currentBuyToken === 'BNB') {
              tx = await router.swapExactETHForTokensSupportingFeeOnTransferTokens(
          buyAmount,
                newMinTokens,
                transactionPath,
                wallet.address,
                Math.floor(Date.now() / 1000) + 60 * 20,
                { ...txParams, value: buyAmount }
              );
            } else {
              tx = await router.swapExactTokensForTokensSupportingFeeOnTransferTokens(
                buyAmount,
                newMinTokens,
                transactionPath,
          wallet.address,
          Math.floor(Date.now() / 1000) + 60 * 20,
          txParams
        );
            }
            
            console.log(`使用更高滑点交易成功，哈希: ${tx.hash}`);
          } catch (retryError) {
            console.error(`使用更高滑点重试也失败:`, retryError.message);
            
            // 再尝试使用备用路径
            try {
              // 使用不同的中间代币路径
              console.log(`尝试使用备用路径和更高滑点...`);
              
              // 准备备用路径
              let backupPaths = [];
              
              if (transactionPath.length === 2) {
                // 当前是直接路径，尝试加入中间代币
                backupPaths = [
                  [transactionPath[0], DEFAULT_TOKENS.BNB.address, transactionPath[1]],  // 通过BNB
                  [transactionPath[0], DEFAULT_TOKENS.USDT.address, transactionPath[1]]  // 通过USDT
                ];
              } else {
                // 当前是间接路径，尝试不同的中间代币
                const interToken = transactionPath[1] === DEFAULT_TOKENS.BNB.address ? 
                  DEFAULT_TOKENS.USDT.address : DEFAULT_TOKENS.BNB.address;
                backupPaths = [
                  [transactionPath[0], interToken, transactionPath[2]]
                ];
              }
              
              // 依次尝试备用路径
              for (let i = 0; i < backupPaths.length; i++) {
                const path = backupPaths[i];
                try {
                  console.log(`尝试备用路径 ${i+1}: [${path.map(a => {
                    if (a === DEFAULT_TOKENS[currentBuyToken].address) return currentBuyToken;
                    if (a === tokenAddress) return tokenInfo.symbol;
                    if (a === DEFAULT_TOKENS.USDT.address) return 'USDT';
                    if (a === DEFAULT_TOKENS.BNB.address) return 'BNB';
                    return a.substring(0, 6) + '...' + a.substring(a.length - 4);
                  }).join(' -> ')}]`);
                  
                  // 使用更高滑点和备用路径
                  const backupTx = await router.swapExactTokensForTokensSupportingFeeOnTransferTokens(
                    buyAmount,
                    newMinTokens,
                    path,
                    wallet.address,
                    Math.floor(Date.now() / 1000) + 60 * 20,
                    { ...txParams, gasLimit: 1000000 } // 使用最高gas限制
                  );
                  
                  console.log(`备用路径交易成功，哈希: ${backupTx.hash}`);
                  tx = backupTx;
                  break; // 成功后跳出循环
                } catch (pathError) {
                  console.error(`备用路径 ${i+1} 失败:`, pathError.message);
                  // 继续尝试下一个路径
                }
              }
              
              // 如果所有备用路径都失败，则抛出原始错误
              if (!tx) {
                throw retryError;
              }
            } catch (backupError) {
              console.error(`所有备用路径都失败:`, backupError.message);
              throw retryError; // 重试失败，抛出错误
            }
          }
        } else {
          // 非滑点/路径问题，直接抛出
          throw transactionError;
        }
      }
      
      setActiveTx(prev => ({
        ...prev,
        status: '等待确认'
      }));
      
      // 改为轮询方式检查交易确认状态
      let receipt = null;
      let confirmationAttempts = 0;
      const maxAttempts = 120; // 最多等待120秒
      
      while (confirmationAttempts < maxAttempts) {
        try {
          // 每秒检查一次交易状态
          receipt = await rpcProvider.getTransactionReceipt(tx.hash);
          
          // 如果交易已确认，跳出循环
          if (receipt && receipt.blockNumber) {
            break;
          }
          
          // 更新状态显示等待时间
          setActiveTx(prev => ({
            ...prev,
            status: `等待确认 (${confirmationAttempts}秒)`
          }));
          
          // 等待1秒再次检查
          await new Promise(resolve => setTimeout(resolve, 1000));
          confirmationAttempts++;
          
        } catch (err) {
          console.error("检查交易确认状态失败:", err);
          // 错误时也等待1秒再重试
          await new Promise(resolve => setTimeout(resolve, 1000));
          confirmationAttempts++;
        }
      }
      
      // 检查是否获取到确认收据
      if (!receipt) {
        setActiveTx(prev => ({
          ...prev,
          status: '确认超时，请检查链上状态'
        }));
        
        const txRecord = {
          wallet: wallet.address,
          type: isVolumeTrade ? `做市买入 (使用${currentBuyToken})` : `买入 (使用${currentBuyToken})`,
          spentAmount: formattedAmount,
          spentSymbol: currentBuyToken,
          receivedAmount: '待确认',
          receivedSymbol: tokenInfo.symbol,
          txHash: tx.hash,
          explorerUrl: `https://bscscan.com/tx/${tx.hash}`,
          timestamp: new Date().toLocaleString(),
          status: '确认超时'
        };
        
        logTransaction(txRecord);
        return false;
      }
      
      if (receipt.status === 1) {
        updateTransactionStatus(prev => ({
          ...prev,
          successCount: prev.successCount + 1
        }));
        
        // 先更新活动交易状态为已完成
        setActiveTx(prev => ({
          ...prev,
          status: '交易已确认'
        }));
        
        // 获取交易后的代币余额
        const afterTokenBalance = await tokenContract.balanceOf(wallet.address);
        
        // 计算实际收到的代币数量
        const tokenBalanceDiff = BigInt(afterTokenBalance.toString()) - BigInt(beforeTokenBalance.toString());
        const receivedTokens = ethers.formatUnits(tokenBalanceDiff, tokenInfo.decimals);
        
        console.log(`---------- 买入交易结果 ----------`);
        console.log(`交易前余额: ${ethers.formatUnits(beforeTokenBalance, tokenInfo.decimals)} ${tokenInfo.symbol}`);
        console.log(`交易后余额: ${ethers.formatUnits(afterTokenBalance, tokenInfo.decimals)} ${tokenInfo.symbol}`);
        console.log(`实际获得: ${receivedTokens} ${tokenInfo.symbol}`);
        console.log(`预期获得: ${ethers.formatUnits(expectedTokens, tokenInfo.decimals)} ${tokenInfo.symbol}`);
        
        // 计算实际收益率
        const expectedTokensFloat = parseFloat(ethers.formatUnits(expectedTokens, tokenInfo.decimals));
        const receivedTokensFloat = parseFloat(receivedTokens);
        const effectiveRate = (receivedTokensFloat / expectedTokensFloat * 100).toFixed(2);
        console.log(`实际收益率: ${effectiveRate}% (相对于预期)`);
        
        // 更新交易记录，确保receivedAmount有值
        const txRecord = {
          wallet: wallet.address,
          type: isVolumeTrade ? `做市买入 (使用${currentBuyToken})` : `买入 (使用${currentBuyToken})`,
          spentAmount: formattedAmount,
          spentSymbol: currentBuyToken,
          receivedAmount: receivedTokens,
          receivedSymbol: tokenInfo.symbol,
          beforeBalance: ethers.formatUnits(beforeTokenBalance, tokenInfo.decimals),
          afterBalance: ethers.formatUnits(afterTokenBalance, tokenInfo.decimals),
          txHash: tx.hash,
          explorerUrl: `https://bscscan.com/tx/${tx.hash}`,
          timestamp: new Date().toLocaleString(),
          status: '已完成',
          path: transactionPath.map(addr => {
            if (addr === DEFAULT_TOKENS[currentBuyToken].address) return currentBuyToken;
            if (addr === tokenAddress) return tokenInfo.symbol;
            if (addr === DEFAULT_TOKENS.USDT.address) return 'USDT';
            if (addr === DEFAULT_TOKENS.BNB.address) return 'BNB';
            return addr.substring(0, 6) + '...' + addr.substring(addr.length - 4);
          }).join(' -> ')
        };
        
        logTransaction(txRecord);
        
        // 更新钱包余额
        await fetchWalletsBalances([wallet]);
        
        // 交易完成后清除活动交易状态
        setTimeout(() => {
          setActiveTx(prev => {
            // 只有当当前活动交易与此交易匹配时才清除
            if (prev && prev.wallet === wallet.address && prev.type === `买入 (使用${currentBuyToken})`) {
              return null;
            }
            return prev;
          });
        }, 3000); // 保留3秒后清除
        
        return true;
      } else {
        // 交易失败
        setActiveTx(prev => ({
          ...prev,
          status: '交易失败'
        }));
        
        updateTransactionStatus(prev => ({
          ...prev,
          failCount: prev.failCount + 1,
          lastError: '交易状态为失败'
        }));
        
        const txRecord = {
          wallet: wallet.address,
          type: isVolumeTrade ? `做市买入 (使用${currentBuyToken})` : `买入 (使用${currentBuyToken})`,
          spentAmount: formattedAmount,
          spentSymbol: currentBuyToken,
          receivedAmount: '0',
          receivedSymbol: tokenInfo.symbol,
          txHash: tx.hash,
          explorerUrl: `https://bscscan.com/tx/${tx.hash}`,
          timestamp: new Date().toLocaleString(),
          status: '失败',
          error: '交易执行失败'
        };
        
        logTransaction(txRecord);
        throw new Error('交易状态为失败');
      }
    } catch (err) {
      console.error(`钱包 ${wallet.address} 买入交易(使用${buyTokenType})失败:`, err);
      
      // 创建更具体的错误消息
      let errorMessage = err.message || '未知错误';
      let errorType = 'unknown';
      
      if (errorMessage.includes('insufficient funds')) {
        errorMessage = 'BNB余额不足以支付Gas费';
        errorType = 'gas';
      } else if (errorMessage.includes('user rejected')) {
        errorMessage = '用户拒绝交易';
        errorType = 'user';
      } else if (errorMessage.includes('TRANSFER_FAILED')) {
        errorMessage = '代币转移失败 - 可能原因: 1) 代币合约有转账限制或税费 2) 滑点设置过低 3) 交易金额过大';
        errorType = 'transfer';
        
        // 显示更详细的错误解释
        showMessage(`代币转移失败: 代币可能有特殊的交易机制，建议尝试: 1) 减少交易金额 2) 增加滑点设置至15-30% 3) 选择不同的交易对`, 'error', 8000);
      } else if (errorMessage.includes('transaction failed')) {
        errorMessage = '交易执行失败，链上逻辑拒绝了交易';
        errorType = 'execution';
      }
      
      // 记录失败的交易
      const txRecord = {
        wallet: wallet.address,
        type: isVolumeTrade ? `做市买入 (使用${buyTokenType})` : `买入 (使用${buyTokenType})`,
        spentAmount: amount,
        spentSymbol: buyTokenType,
        receivedAmount: '0',
        receivedSymbol: tokenInfo.symbol,  // 确保即使交易失败也记录目标代币符号
        error: errorMessage,
        errorType: errorType,  // 添加错误类型
        timestamp: new Date().toLocaleString(),
        status: '失败'
      };
      
      logTransaction(txRecord);
      
      // 自动暂停交易以防止继续进行可能会失败的交易
      // 只在严重错误时暂停交易
      if (['gas', 'execution', 'transfer'].includes(errorType)) {
      pauseAllTransactions();
        showMessage(`由于${errorMessage}，自动暂停所有交易`, 'warning');
      }
      
      return false;
    } finally {
      if (currentIndex === totalCount) {
        resetTransactionStatus();
      }
    }
  };

  // 执行卖出交易 - 支持不同的卖出代币
  const processSellTransaction = async (wallet, percentage, currentIndex, totalCount, isVolumeTrade = false, customSlippage = null, sellTokenType = 'USDT') => {
    // 检查是否暂停交易
    if (transactionsPaused) {
      console.log(`交易已暂停，跳过卖出交易 ${wallet.address}`);
      return false;
    }

    try {
      // 严格使用用户选择的代币
      const currentSellToken = sellTokenType;
      console.log(`卖出代币到${currentSellToken}，精度: ${DEFAULT_TOKENS[currentSellToken].decimals}`);

      // 记录交易前余额
      let beforeBalance;
      if (currentSellToken === 'BNB') {
        beforeBalance = await rpcProvider.getBalance(wallet.address);
      } else {
        const sellTokenContract = new ethers.Contract(DEFAULT_TOKENS[currentSellToken].address, ERC20_ABI, rpcProvider);
        beforeBalance = await sellTokenContract.balanceOf(wallet.address);
      }

      updateTransactionStatus({
        isProcessing: true,
        currentOperation: `卖出交易 (换${currentSellToken})`,
        progress: currentIndex,
        total: totalCount
      });

      // 验证百分比参数
      const sellPercentage = parseFloat(percentage);
      if (isNaN(sellPercentage) || sellPercentage <= 0 || sellPercentage > 100) {
        throw new Error(`无效的卖出百分比: ${percentage}，必须在0-100之间`);
      }
      
      // 使用rpcProvider检查钱包中的代币余额
      const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, rpcProvider);
      const tokenBalance = await tokenContract.balanceOf(wallet.address);
      
      if (tokenBalance <= 0) {
        throw new Error(`钱包 ${wallet.address} 代币余额为0，无法卖出`);
      }
      
      // 计算要卖出的代币数量
      const sellAmount = tokenBalance * BigInt(Math.floor(sellPercentage * 100)) / 10000n;
      
      console.log(`---------- 卖出计算详情 ----------`);
      console.log(`钱包地址: ${wallet.address}`);
      console.log(`代币余额: ${ethers.formatUnits(tokenBalance, tokenInfo.decimals)} ${tokenInfo.symbol}`);
      console.log(`卖出百分比: ${sellPercentage}%`);
      console.log(`卖出数量: ${ethers.formatUnits(sellAmount, tokenInfo.decimals)} ${tokenInfo.symbol}`);
      console.log(`---------- 卖出计算结束 ----------`);
      
      if (sellAmount <= 0) {
        throw new Error(`计算的卖出数量为0，无法执行交易`);
      }
      
      // 获取预期收到的代币数量
      const expectedTokens = await calculateExpectedSellAmount(sellAmount, currentSellToken);
      const slippagePercent = parseFloat(customSlippage || (isVolumeTrade ? volumeSellSlippage : sellSlippage));
      const minTokens = expectedTokens * BigInt(Math.floor((100 - slippagePercent) * 100)) / 10000n;
      
      // 设置当前活动交易
      setActiveTx({
        wallet: wallet.address,
        type: `卖出 (换${currentSellToken})`,
        amount: `${ethers.formatUnits(sellAmount, tokenInfo.decimals)} ${tokenInfo.symbol} (${sellPercentage}%)`,
        status: '检查授权中',
        index: currentIndex,
        total: totalCount,
        isVolumeTrade
      });
      
      // 使用私钥和rpcProvider创建签名者
      const walletSigner = new ethers.Wallet(wallet.privateKey, rpcProvider);
      const router = new ethers.Contract(PANCAKE_ROUTER_V2, PANCAKE_ROUTER_ABI, walletSigner);
      
      // 检查代币的授权
      const allowance = await tokenContract.allowance(wallet.address, PANCAKE_ROUTER_V2);
      
      if (allowance < sellAmount) {
        await handleTokenApproval(tokenContract, PANCAKE_ROUTER_V2, wallet.address, gasPrice);
      }
      
      // 获取当前gas价格
      const gasPrice = await rpcProvider.getFeeData();
      const txParams = {
        gasLimit: 500000,
        gasPrice: useCustomGas && customGasPrice ? 
          ethers.parseUnits(customGasPrice, 'gwei') : 
          gasPrice.gasPrice * 5n
      };
      
      let tx;
      
      // 根据不同代币执行不同的交易
      if (currentSellToken === 'BNB') {
        setActiveTx(prev => ({
          ...prev,
          status: '执行卖出交易 (换BNB)'
        }));
        
        console.log(`---------- 执行BNB卖出交易 ----------`);
        console.log(`使用方法: swapExactTokensForETH`);
        console.log(`卖出数量: ${ethers.formatUnits(sellAmount, tokenInfo.decimals)} ${tokenInfo.symbol}`);
        console.log(`最低获得: ${ethers.formatUnits(minTokens, DEFAULT_TOKENS.BNB.decimals)} BNB`);
        console.log(`交易路径: [${tokenAddress}, ${DEFAULT_TOKENS.BNB.address}]`);
        
        tx = await router.swapExactTokensForETH(
          sellAmount,
          minTokens,
          [tokenAddress, DEFAULT_TOKENS.BNB.address],
          wallet.address,
          Math.floor(Date.now() / 1000) + 60 * 20,
          txParams
        );
      } else if (currentSellToken === 'FIST') {
        setActiveTx(prev => ({
          ...prev,
          status: '执行卖出交易 (换FIST)'
        }));
        
        console.log(`---------- 执行FIST卖出交易 ----------`);
        console.log(`使用方法: swapExactTokensForTokensSupportingFeeOnTransferTokens (支持转账费机制)`);
        console.log(`卖出数量: ${ethers.formatUnits(sellAmount, tokenInfo.decimals)} ${tokenInfo.symbol}`);
        console.log(`最低获得: ${ethers.formatUnits(minTokens, DEFAULT_TOKENS.FIST.decimals)} FIST`);
        console.log(`交易路径: [${tokenAddress}, ${DEFAULT_TOKENS.FIST.address}]`);
        
        // 提高FIST交易的gas限制，因为FIST可能有更复杂的转账逻辑
        txParams.gasLimit = 750000;
        
        // 获取交易前FIST余额
        const fistContract = new ethers.Contract(DEFAULT_TOKENS.FIST.address, ERC20_ABI, rpcProvider);
        const beforeFistBalance = await fistContract.balanceOf(wallet.address);
        console.log(`交易前FIST余额: ${ethers.formatUnits(beforeFistBalance, DEFAULT_TOKENS.FIST.decimals)} FIST`);
        
        try {
          // 使用直接路径和支持转账费的方法
          tx = await router.swapExactTokensForTokensSupportingFeeOnTransferTokens(
            sellAmount,
            minTokens,
            [tokenAddress, DEFAULT_TOKENS.FIST.address],
            wallet.address,
            Math.floor(Date.now() / 1000) + 60 * 20,
            txParams
          );
          console.log(`FIST交易提交成功, 哈希: ${tx.hash}`);
        } catch (swapError) {
          // 如果支持转账费的方法失败，记录错误并尝试使用多跳路径
          console.error(`FIST直接交易失败: ${swapError.message}`);
          
          if (swapError.message.includes('INSUFFICIENT_OUTPUT_AMOUNT') || 
              swapError.message.includes('execution reverted')) {
            
            console.log(`尝试使用多跳路径通过USDT交易到FIST...`);
            // 使用多跳路径: 目标代币 -> USDT -> FIST
        tx = await router.swapExactTokensForTokensSupportingFeeOnTransferTokens(
          sellAmount,
          minTokens,
          [tokenAddress, DEFAULT_TOKENS.USDT.address, DEFAULT_TOKENS.FIST.address],
          wallet.address,
          Math.floor(Date.now() / 1000) + 60 * 20,
          txParams
        );
            console.log(`多跳路径FIST交易提交成功, 哈希: ${tx.hash}`);
          } else {
            // 其他错误则抛出
            throw swapError;
          }
        }
        
        // 保存beforeBalance变量供后续使用
        beforeBalance = beforeFistBalance;
      } else if (currentSellToken === 'USDT') {
        setActiveTx(prev => ({
          ...prev,
          status: '执行卖出交易 (换USDT)'
        }));
        
        console.log(`---------- 执行USDT卖出交易 ----------`);
        console.log(`使用方法: swapExactTokensForTokensSupportingFeeOnTransferTokens`);
        console.log(`卖出数量: ${ethers.formatUnits(sellAmount, tokenInfo.decimals)} ${tokenInfo.symbol}`);
        console.log(`最低获得: ${ethers.formatUnits(minTokens, DEFAULT_TOKENS.USDT.decimals)} USDT`);
        console.log(`交易路径: [${tokenAddress}, ${DEFAULT_TOKENS.USDT.address}]`);
        
        // 使用直接路径
        tx = await router.swapExactTokensForTokensSupportingFeeOnTransferTokens(
          sellAmount,
          minTokens,
          [tokenAddress, DEFAULT_TOKENS.USDT.address],
          wallet.address,
          Math.floor(Date.now() / 1000) + 60 * 20,
          txParams
        );
      } else {
        setActiveTx(prev => ({
          ...prev,
          status: '执行卖出交易'
        }));
        
        console.log(`---------- 执行${currentSellToken}卖出交易 ----------`);
        console.log(`使用默认方法: swapExactTokensForTokens`);
        console.log(`卖出数量: ${ethers.formatUnits(sellAmount, tokenInfo.decimals)} ${tokenInfo.symbol}`);
        console.log(`最低获得: ${ethers.formatUnits(minTokens, DEFAULT_TOKENS[currentSellToken].decimals)} ${currentSellToken}`);
        console.log(`交易路径: [${tokenAddress}, ${DEFAULT_TOKENS[currentSellToken].address}]`);
        
        tx = await router.swapExactTokensForTokens(
          sellAmount,
          minTokens,
          [tokenAddress, DEFAULT_TOKENS[currentSellToken].address],
          wallet.address,
          Math.floor(Date.now() / 1000) + 60 * 20,
          txParams
        );
      }
      
      console.log(`交易已提交, 哈希: ${tx.hash}`);
      
      setActiveTx(prev => ({
        ...prev,
        status: '等待交易确认'
      }));
      
      // 改为轮询方式检查交易确认状态
      let receipt = null;
      let confirmationAttempts = 0;
      const maxAttempts = 120; // 最多等待120秒
      
      while (confirmationAttempts < maxAttempts) {
        try {
          // 每秒检查一次交易状态
          receipt = await rpcProvider.getTransactionReceipt(tx.hash);
          
          // 如果交易已确认，跳出循环
          if (receipt && receipt.blockNumber) {
            break;
          }
          
          // 更新状态显示等待时间
          setActiveTx(prev => ({
            ...prev,
            status: `等待确认 (${confirmationAttempts}秒)`
          }));
          
          // 等待1秒再次检查
          await new Promise(resolve => setTimeout(resolve, 1000));
          confirmationAttempts++;
          
        } catch (err) {
          console.error("检查交易确认状态失败:", err);
          // 错误时也等待1秒再重试
          await new Promise(resolve => setTimeout(resolve, 1000));
          confirmationAttempts++;
        }
      }
      
      // 检查是否获取到确认收据
      if (!receipt) {
        setActiveTx(prev => ({
          ...prev,
          status: '确认超时，请检查链上状态'
        }));
        
        const txRecord = {
          wallet: wallet.address,
          type: isVolumeTrade ? `做市卖出 (获得${currentSellToken})` : `卖出 (获得${currentSellToken})`,
          spentAmount: ethers.formatUnits(sellAmount, tokenInfo.decimals),
          spentSymbol: tokenInfo.symbol,
          receivedAmount: '待确认',
          receivedSymbol: currentSellToken,
          txHash: tx.hash,
          explorerUrl: `https://bscscan.com/tx/${tx.hash}`,
          timestamp: new Date().toLocaleString(),
          status: '确认超时'
        };
        
        logTransaction(txRecord);
        return false;
      }
      
      if (receipt.status === 1) {
        updateTransactionStatus(prev => ({
          ...prev,
          successCount: prev.successCount + 1
        }));
        
        // 先更新活动交易状态为已完成
        setActiveTx(prev => ({
          ...prev,
          status: '交易已确认'
        }));
        
        // 获取交易后余额并计算收益
        let afterBalance;
        let receivedAmount;
        
        if (currentSellToken === 'BNB') {
          afterBalance = await rpcProvider.getBalance(wallet.address);
          receivedAmount = ethers.formatUnits(
            BigInt(afterBalance.toString()) - BigInt(beforeBalance.toString()),
            DEFAULT_TOKENS[currentSellToken].decimals
          );
        } else if (currentSellToken === 'FIST') {
          // 专门为FIST交易计算实际收到的代币数量
          const fistContract = new ethers.Contract(DEFAULT_TOKENS.FIST.address, ERC20_ABI, rpcProvider);
          afterBalance = await fistContract.balanceOf(wallet.address);
          
          // 计算差值并格式化
          const balanceDiff = BigInt(afterBalance.toString()) - BigInt(beforeBalance.toString());
          receivedAmount = ethers.formatUnits(balanceDiff, DEFAULT_TOKENS.FIST.decimals);
          
          console.log(`交易后FIST余额: ${ethers.formatUnits(afterBalance, DEFAULT_TOKENS.FIST.decimals)} FIST`);
          console.log(`实际获得FIST: ${receivedAmount} FIST`);
        } else {
          const sellTokenContract = new ethers.Contract(DEFAULT_TOKENS[currentSellToken].address, ERC20_ABI, rpcProvider);
          afterBalance = await sellTokenContract.balanceOf(wallet.address);
          receivedAmount = ethers.formatUnits(
          BigInt(afterBalance.toString()) - BigInt(beforeBalance.toString()),
          DEFAULT_TOKENS[currentSellToken].decimals
        );
        }
        
        // 更新交易记录
        const txRecord = {
          wallet: wallet.address,
          type: isVolumeTrade ? `做市卖出 (获得${currentSellToken})` : `卖出 (获得${currentSellToken})`,
          spentAmount: ethers.formatUnits(sellAmount, tokenInfo.decimals),
          spentSymbol: tokenInfo.symbol,
          receivedAmount: receivedAmount,
          receivedSymbol: currentSellToken,
          beforeBalance: ethers.formatUnits(beforeBalance, DEFAULT_TOKENS[currentSellToken].decimals),
          afterBalance: ethers.formatUnits(afterBalance, DEFAULT_TOKENS[currentSellToken].decimals),
          txHash: tx.hash,
          explorerUrl: `https://bscscan.com/tx/${tx.hash}`,
          timestamp: new Date().toLocaleString(),
          status: '已完成'
        };
        
        logTransaction(txRecord);
        
        // 更新钱包余额
        await fetchWalletsBalances([wallet]);
        
        // 交易完成后清除活动交易状态
        setTimeout(() => {
          setActiveTx(prev => {
            // 只有当当前活动交易与此交易匹配时才清除
            if (prev && prev.wallet === wallet.address && prev.type === `卖出 (获得${currentSellToken})`) {
              return null;
            }
            return prev;
          });
        }, 3000); // 保留3秒后清除
        
        return true;
      } else {
        // 交易失败
        setActiveTx(prev => ({
          ...prev,
          status: '交易失败'
        }));
        
        const txRecord = {
          wallet: wallet.address,
          type: isVolumeTrade ? `做市卖出 (获得${currentSellToken})` : `卖出 (获得${currentSellToken})`,
          spentAmount: ethers.formatUnits(sellAmount, tokenInfo.decimals),
          spentSymbol: tokenInfo.symbol,
          receivedAmount: '0',
          receivedSymbol: currentSellToken,
          txHash: tx.hash,
          explorerUrl: `https://bscscan.com/tx/${tx.hash}`,
          timestamp: new Date().toLocaleString(),
          status: '失败',
          error: '交易执行失败'
        };
        
        logTransaction(txRecord);
        throw new Error('交易状态为失败');
      }
    } catch (err) {
      // 处理错误...
      console.error(`卖出交易出错:`, err);
      
      // 如果是交易失败的错误，则已经记录了交易记录，不需要再次记录
      if (err.message === '交易状态为失败') {
        // 已经记录了交易记录，只需暂停交易
        pauseAllTransactions();
        return false;
      }
      
      // 特殊处理 Pancake: K 错误 - 自动增加滑点重试
      if (err.message && (err.message.includes("Pancake: K") || err.message.includes("INSUFFICIENT_OUTPUT_AMOUNT") || 
          err.message.includes("流动性不足") || err.message.includes("需要更高滑点"))) {
        console.log(`检测到 Pancake: K 错误，尝试增加滑点重试...`);
        
        // 如果是卖出获得FIST，尝试使用不同的路径
        if (sellTokenType === 'FIST') {
          console.log(`检测到卖出获得FIST失败，尝试备用路径...`);
          
          // 确保我们有正确的amountWei值 - 如果错误处理函数中没有定义，需要重新计算
          // 从函数主体获取代币余额和卖出比例以计算amountWei
          const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, rpcProvider);
          const currentTokenBalance = await tokenContract.balanceOf(wallet.address);
          const sellPercentageValue = parseFloat(percentage);
          const sellAmountWei = (currentTokenBalance * BigInt(Math.floor(sellPercentageValue * 100))) / 10000n;
          
          // 记录交易前FIST余额
          let beforeFistBalance;
          try {
            const fistContract = new ethers.Contract(DEFAULT_TOKENS.FIST.address, ERC20_ABI, rpcProvider);
            beforeFistBalance = await fistContract.balanceOf(wallet.address);
            console.log(`备用路径交易前FIST余额: ${ethers.formatUnits(beforeFistBalance, DEFAULT_TOKENS.FIST.decimals)} FIST`);
          } catch (balErr) {
            console.error("获取FIST余额失败:", balErr);
            beforeFistBalance = 0n;
          }
          
          // 尝试不同的路径组合
          const backupPaths = [
            // 主路径: 直接到FIST
            [tokenAddress, DEFAULT_TOKENS.FIST.address],
            // 备用路径1: 通过BNB
            [tokenAddress, DEFAULT_TOKENS.BNB.address, DEFAULT_TOKENS.FIST.address],
            // 备用路径2: 通过USDT
            [tokenAddress, DEFAULT_TOKENS.USDT.address, DEFAULT_TOKENS.FIST.address]
          ];
          
          // 使用更高滑点
          const currentSlippage = parseFloat(customSlippage || (isVolumeTrade ? volumeSellSlippage : sellSlippage));
          const newSlippage = Math.max(currentSlippage * 3, 30); // 滑点至少30%
          
          console.log(`尝试使用${newSlippage}%滑点和多种路径重试`);
          
          // 获取并调整gas价格 - 确保txParams在此处定义
          const retryGasPrice = await rpcProvider.getFeeData();
          const retryTxParams = {
            gasLimit: 600000,
            gasPrice: useCustomGas && customGasPrice ? 
              ethers.parseUnits(customGasPrice, 'gwei') : 
              retryGasPrice.gasPrice * 5n
          };
          
          // 依次尝试每个备用路径
          for (let i = 0; i < backupPaths.length; i++) {
            const path = backupPaths[i];
            console.log(`尝试备用路径 ${i+1}/${backupPaths.length}: ${path.map(addr => {
              if (addr === tokenAddress) return tokenInfo.symbol;
              if (addr === DEFAULT_TOKENS.USDT.address) return 'USDT';
              if (addr === DEFAULT_TOKENS.BNB.address) return 'BNB';
              if (addr === DEFAULT_TOKENS.BUSD.address) return 'BUSD';
              if (addr === DEFAULT_TOKENS.FIST.address) return 'FIST';
              return addr;
            }).join(' -> ')}`);
            
            try {
              // 设置活动交易状态
              setActiveTx(prev => ({
                ...prev,
                status: `尝试备用路径 ${i+1}`
              }));
              
              // 使用路径和更高滑点重试交易
              const walletSigner = new ethers.Wallet(wallet.privateKey, rpcProvider);
              const router = new ethers.Contract(PANCAKE_ROUTER_V2, PANCAKE_ROUTER_ABI, walletSigner);
              
              // 获取预期收到的代币数量
              const expectedOut = await router.getAmountsOut(sellAmountWei, path);
              const minOut = expectedOut[expectedOut.length - 1] * BigInt(Math.floor((100 - newSlippage) * 100)) / 10000n;
              
              console.log(`预期获得: ${ethers.formatUnits(expectedOut[expectedOut.length - 1], DEFAULT_TOKENS.FIST.decimals)} FIST，最低接受: ${ethers.formatUnits(minOut, DEFAULT_TOKENS.FIST.decimals)} FIST`);
              
              // 执行交易 - 使用支持费用的函数
              const retryTx = await router.swapExactTokensForTokensSupportingFeeOnTransferTokens(
                sellAmountWei,
                minOut,
                path,
                wallet.address,
                Math.floor(Date.now() / 1000) + 60 * 20, // 20分钟截止时间
                retryTxParams
              );
              
              // 等待交易确认
              const receipt = await retryTx.wait();
              
              if (receipt.status === 1) {
                console.log(`备用路径 ${i+1} 交易成功!`);
                
                // 获取交易后余额并计算收益
                const fistContract = new ethers.Contract(DEFAULT_TOKENS.FIST.address, ERC20_ABI, rpcProvider);
                const afterFistBalance = await fistContract.balanceOf(wallet.address);
                
                // 确保计算差值时使用BigInt类型
                const beforeBalanceBigInt = BigInt(beforeFistBalance.toString());
                const afterBalanceBigInt = BigInt(afterFistBalance.toString());
                const balanceDiff = afterBalanceBigInt - beforeBalanceBigInt;
                
                // 格式化获得的FIST数量
                const receivedAmount = ethers.formatUnits(balanceDiff, DEFAULT_TOKENS.FIST.decimals);
                
                console.log(`交易后FIST余额: ${ethers.formatUnits(afterFistBalance, DEFAULT_TOKENS.FIST.decimals)} FIST`);
                console.log(`实际获得FIST: ${receivedAmount} FIST (备用路径)`);
                
                // 记录成功交易
                const txRecord = {
                  wallet: wallet.address,
                  type: isVolumeTrade ? `做市卖出 (获得FIST-备用路径)` : `卖出 (获得FIST-备用路径)`,
                  spentAmount: ethers.formatUnits(sellAmountWei, tokenInfo.decimals),
                  spentSymbol: tokenInfo.symbol,
                  receivedAmount: receivedAmount,
                  receivedSymbol: 'FIST',
                  beforeBalance: ethers.formatUnits(beforeFistBalance, DEFAULT_TOKENS.FIST.decimals),
                  afterBalance: ethers.formatUnits(afterFistBalance, DEFAULT_TOKENS.FIST.decimals),
                  txHash: retryTx.hash,
                  explorerUrl: `https://bscscan.com/tx/${retryTx.hash}`,
                  timestamp: new Date().toLocaleString(),
                  status: '已完成',
                  route: `备用路径${i+1}: ${path.map(a => a === tokenAddress ? tokenInfo.symbol : (a === DEFAULT_TOKENS.FIST.address ? 'FIST' : (a === DEFAULT_TOKENS.USDT.address ? 'USDT' : 'BNB'))).join(' -> ')}`
                };
                
                logTransaction(txRecord);
                return true;
              }
            } catch (retryErr) {
              console.error(`备用路径 ${i+1} 失败:`, retryErr.message);
              // 继续尝试下一个路径
            }
          }
          
          console.log(`所有备用路径都失败，无法完成交易`);
        } else {
          // 对于非FIST卖出，使用常规的滑点增加方法
          // 大幅增加滑点 - 将滑点增加到当前值的3倍
          const currentSlippage = parseFloat(customSlippage || (isVolumeTrade ? volumeSellSlippage : sellSlippage));
          const newSlippage = currentSlippage * 3; // 滑点增加到3倍
          
          console.log(`将滑点从 ${currentSlippage}% 增加到 ${newSlippage}%`);
          
          // 自动重试一次交易
          try {
            return await processSellTransaction(
              wallet, 
              sellPercentage, 
              currentIndex, 
              totalCount, 
              isVolumeTrade, 
              newSlippage, 
              sellTokenType
            );
          } catch (retryErr) {
            console.error(`使用更高滑点(${newSlippage}%)重试仍然失败:`, retryErr);
            // 记录最终失败的错误
            err = retryErr;
          }
        }
      }
      
      // 创建一个更清晰的错误信息
      let errorMessage = err.message;
      
      // 为一些常见错误提供更友好的消息
      if (err.message.includes("insufficient funds")) {
        errorMessage = "余额不足以支付Gas费";
      } else if (err.message.includes("user rejected")) {
        errorMessage = "用户拒绝交易";
      } else if (err.message.includes("execution reverted")) {
        errorMessage = "交易执行被链上逻辑拒绝";
      } else if (err.message.includes("Pancake: K")) {
        errorMessage = `滑点不足(${customSlippage || sellSlippage}%)，流动性问题。尝试增加滑点或减小交易量`;
      }
      
      try {
        // 创建一个交易记录，但标记为失败
        const errorTxRecord = {
          wallet: wallet.address,
          type: `卖出 (获得${sellTokenType})`,
          spentAmount: '0', // 不再使用amountWei，直接设为0
          spentSymbol: tokenInfo.symbol,
          receivedAmount: '0',
          receivedSymbol: sellTokenType,
          txHash: err.transactionHash || '',
          explorerUrl: err.transactionHash ? `https://bscscan.com/tx/${err.transactionHash}` : '',
          timestamp: new Date().toLocaleString(),
          status: '失败',
          error: errorMessage
        };
        
        logTransaction(errorTxRecord);
        
        // 自动暂停交易以防止继续进行可能会失败的交易
        pauseAllTransactions();
        
      } catch (logErr) {
        console.error("记录交易错误时出错:", logErr);
      }
      
      handleError(err, `钱包 ${wallet.address} 卖出交易(获得${sellTokenType})`);
      return false;
    } finally {
      if (currentIndex === totalCount) {
        resetTransactionStatus();
      }
    }
  };

  // 批量买入 - 支持不同的买入代币
  const batchBuy = async () => {
    if (!tokenAddress || selectedWallets.length === 0) {
      handleError(new Error("请先输入代币地址并选择钱包"), "批量买入");
      return;
    }

    if (!rpcProvider) {
      handleError(new Error("请先选择RPC节点"), "批量买入");
      return;
    }

    // 验证购买金额
    const parsedBuyAmount = useRandomBuyAmount ? 
      parseFloat(buyAmountRange.min) : parseFloat(buyAmount);
    
    if (isNaN(parsedBuyAmount) || parsedBuyAmount <= 0) {
      handleError(new Error(`无效的购买金额: ${useRandomBuyAmount ? buyAmountRange.min : buyAmount}`), "批量买入");
      return;
    }
    
    // 如果使用随机金额，验证最大值
    if (useRandomBuyAmount) {
      const maxAmount = parseFloat(buyAmountRange.max);
      if (isNaN(maxAmount) || maxAmount <= 0 || maxAmount < parsedBuyAmount) {
        handleError(new Error(`无效的最大购买金额: ${buyAmountRange.max}`), "批量买入");
        return;
      }
    }

    // 重置交易暂停状态
    setTransactionsPaused(false);

    try {
      setBatchBuyLoading(true);
      setError(null);
      
      // 重置交易状态
      updateTransactionStatus({
        isProcessing: true,
        currentOperation: '批量买入',
        progress: 0,
        total: selectedWallets.length,
        successCount: 0,
        failCount: 0,
        lastError: null
      });
      
      // 获取实时价格和流动性
      const latestPrice = await fetchPriceAndLiquidity();
      
      if (!latestPrice || latestPrice.status !== 'Active') {
        throw new Error("该代币没有活跃的流动性");
      }
      
      // 直接使用已选择的钱包对象列表，而不是通过地址查找
      const selectedWalletsList = wallets.filter(w => w.isSelected);
      const walletCount = selectedWalletsList.length;
      let successCount = 0;
      
      // 创建交易列表，包含钱包和金额
      const transactions = selectedWalletsList.map((wallet, index) => {
        let txAmount;
        
        if (useRandomBuyAmount) {
          // 生成随机金额
          txAmount = getRandomAmount(
            parseFloat(buyAmountRange.min), 
            parseFloat(buyAmountRange.max),
            DEFAULT_TOKENS[buyToken].decimals
          );
          
          // 再次验证随机金额是否有效
          if (isNaN(txAmount) || txAmount <= 0) {
            console.error(`生成的随机金额无效: ${txAmount}，使用最小值代替`);
            txAmount = parseFloat(buyAmountRange.min);
          }
        } else {
          // 使用固定金额
          txAmount = parseFloat(buyAmount);
        }
        
        // 最终安全检查
        if (isNaN(txAmount) || txAmount <= 0) {
          console.error(`最终交易金额无效: ${txAmount}，使用默认值0.01`);
          txAmount = 0.01;
        }
          
        return {
          wallet,
          amount: txAmount.toString(),  // 转换为字符串确保后续处理不会出问题
          index: index + 1
        };
      });
      
      // 检查是否有有效的交易
      if (transactions.length === 0) {
        throw new Error("没有找到有效的钱包，请确保已导入私钥并选择钱包");
      }
      
      if (buySequentialMode) {
        // 顺序执行
        for (let i = 0; i < transactions.length; i++) {
          // 检查是否暂停交易
          if (transactionsPaused) {
            console.log("交易已暂停，停止批量买入");
            break;
          }
          
          const { wallet, amount, index } = transactions[i];
          const success = await processBuyTransaction(wallet, amount, index, walletCount, false, null, buyToken);
          
          if (success) {
            successCount++;
          }
          
          // 添加延迟，除非是最后一个交易
          if (i < transactions.length - 1 && !transactionsPaused) {
            await new Promise(resolve => setTimeout(resolve, buyDelayBetweenTx * 1000));
          }
        }
      } else {
        // 并行执行
        const promises = transactions.map(({ wallet, amount, index }) => {
          // 不立即执行，而是返回一个函数
          return async () => {
            // 检查是否暂停交易
            if (transactionsPaused) {
              console.log(`交易已暂停，跳过买入交易 ${wallet.address}`);
              return false;
            }
            return processBuyTransaction(wallet, amount, index, walletCount, false, null, buyToken);
          };
        });
        
        // 使用Promise.all执行所有交易
        const results = await Promise.all(promises.map(fn => fn()));
        
        // 计算成功次数
        successCount = results.filter(success => success).length;
      }
      
      setError(`批量买入完成, 成功: ${successCount}/${selectedWalletsList.length} ${transactionsPaused ? '(交易已暂停)' : ''}`);
    } catch (err) {
      console.error("批量买入失败:", err);
      setError(`批量买入失败: ${err.message}`);
    } finally {
      setBatchBuyLoading(false);
      updateTransactionStatus(prev => ({
        ...prev,
        isProcessing: false
      }));
    }
  };

  // 批量卖出 - 支持不同的卖出代币
  const batchSell = async () => {
    if (!tokenAddress || selectedWallets.length === 0) {
      handleError(new Error("请先输入代币地址并选择钱包"), "批量卖出");
      return;
    }

    if (!rpcProvider) {
      handleError(new Error("请先选择RPC节点"), "批量卖出");
      return;
    }
    
    // 验证卖出百分比
    let minPercentage, maxPercentage;
    
    if (useRandomSellAmount) {
      minPercentage = parseFloat(sellAmountRange.min);
      maxPercentage = parseFloat(sellAmountRange.max);
      
      if (isNaN(minPercentage) || minPercentage <= 0 || minPercentage > 100) {
        handleError(new Error(`最小卖出百分比无效: ${sellAmountRange.min}，必须在0-100之间`), "批量卖出");
        return;
      }
      
      if (isNaN(maxPercentage) || maxPercentage <= 0 || maxPercentage > 100 || maxPercentage < minPercentage) {
        handleError(new Error(`最大卖出百分比无效: ${sellAmountRange.max}，必须在${minPercentage}-100之间`), "批量卖出");
        return;
      }
    } else {
      const sellPercent = parseFloat(sellAmount);
      if (isNaN(sellPercent) || sellPercent <= 0 || sellPercent > 100) {
        handleError(new Error(`卖出百分比无效: ${sellAmount}，必须在0-100之间`), "批量卖出");
        return;
      }
    }

    // 重置交易暂停状态
    setTransactionsPaused(false);

    try {
      setBatchSellLoading(true);
      setError(null);
      
      const selectedWalletsList = wallets.filter(w => w.isSelected);
      const totalCount = selectedWalletsList.length;
      let successCount = 0;
      let failedWallets = [];
      
      // 预先获取最新价格和流动性信息
      await fetchPriceAndLiquidity();
      
      // 创建所有交易Promise数组
      const transactionPromises = selectedWalletsList.map((wallet, i) => {
        // 确定卖出百分比
        let sellPercent;
        if (useRandomSellAmount) {
          // 生成随机百分比，在minPercentage和maxPercentage之间
          sellPercent = getRandomAmount(
            parseFloat(sellAmountRange.min),
            parseFloat(sellAmountRange.max),
            2 // 保留2位小数
          );
          
          // 确保百分比有效
          if (sellPercent > 100) sellPercent = 100;
          if (sellPercent <= 0) sellPercent = 1; // 至少卖出1%
          
          console.log(`钱包 ${wallet.address} 随机卖出百分比: ${sellPercent}%`);
        } else {
          // 使用固定百分比
          sellPercent = parseFloat(sellAmount);
          console.log(`钱包 ${wallet.address} 固定卖出百分比: ${sellPercent}%`);
        }
        
        // 获取当前选择的卖出代币类型
        const currentSellToken = sellToken;
        
        // 计算动态滑点 - 基于流动性状况可能需要更高的滑点
        // 默认使用用户设置的滑点，但会根据流动性和交易规模自动调整
        let dynamicSlippage = parseFloat(sellSlippage);
        
        // 如果流动性非常低（小于1000 USDT），增加滑点
        if (parseFloat(priceInfo.liquidity) < 1000) {
          // 根据流动性大小动态调整滑点
          dynamicSlippage = Math.min(dynamicSlippage * 2, 15); // 最高15%滑点
          console.log(`代币流动性低，自动增加滑点至${dynamicSlippage}%`);
        }
        
        return processSellTransaction(
          wallet,
          sellPercent,
          i + 1,
          totalCount,
          false,
          dynamicSlippage,
          currentSellToken
        ).then(success => {
          if (success) {
            successCount++;
          } else {
            failedWallets.push({ wallet, percentage: sellPercent });
          }
        });
      });
      
      // 如果选择了顺序执行模式，则按顺序执行交易
      if (sellSequentialMode) {
        for (let i = 0; i < transactionPromises.length; i++) {
          // 检查交易是否已暂停
          if (transactionsPaused) {
            console.log("交易已暂停，停止后续交易");
            break;
          }
          
          await transactionPromises[i];
          if (i < transactionPromises.length - 1) {
            await new Promise(resolve => setTimeout(resolve, sellDelayBetweenTx * 1000));
          }
        }
      } else {
        // 否则，所有交易同时执行
        // 使用一种方式允许在暂停时中断操作
        // 创建独立的Promise数组，并在执行前检查暂停状态
        const parallelPromises = [];
        for (let i = 0; i < transactionPromises.length; i++) {
          // 检查交易是否已暂停
          if (transactionsPaused) {
            console.log("交易已暂停，跳过并行交易");
            break;
          }
          parallelPromises.push(transactionPromises[i]);
        }
        
        if (parallelPromises.length > 0) {
          await Promise.all(parallelPromises);
        }
      }
      
      // 如果有失败的钱包且交易未暂停，进行智能重试
      if (failedWallets.length > 0 && !transactionsPaused) {
        console.log(`有 ${failedWallets.length} 个钱包卖出失败，正在智能重试...`);
        
        // 智能重试：分批重试，每批重试前重新获取价格
        const batchSize = Math.min(5, failedWallets.length); // 每批最多5个钱包
        const batches = [];
        
        for (let i = 0; i < failedWallets.length; i += batchSize) {
          batches.push(failedWallets.slice(i, i + batchSize));
        }
        
        for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
          // 检查交易是否已暂停
          if (transactionsPaused) {
            console.log("交易已暂停，停止重试");
            break;
          }
          
          const currentBatch = batches[batchIndex];
          
          // 每批重试前重新获取价格
          await fetchPriceAndLiquidity();
          
          // 获取当前选择的卖出代币类型
          const currentSellToken = sellToken;
          
          // 增加重试的滑点，提高成功率
          const retrySlippage = parseFloat(sellSlippage) * 1.5; // 增加50%的滑点，不再限制最大值
          console.log(`重试交易使用增加的滑点: ${retrySlippage}%`);
          
          // 检查流动性是否极低（小于500 USDT），如果是则使用更激进的滑点
          const liquidityInUsdt = parseFloat(priceInfo.usdtPrice) * parseFloat(priceInfo.liquidity);
          let actualSlippage = retrySlippage;
          
          if (liquidityInUsdt < 500) {
            // 对于极低流动性情况，使用更高的滑点
            actualSlippage = Math.max(retrySlippage * 2, 30); // 至少30%滑点
            console.log(`检测到极低流动性(${liquidityInUsdt.toFixed(2)} USDT)，使用激进滑点: ${actualSlippage}%`);
          }
          
          // 重试当前批次的钱包
          const retryPromises = currentBatch.map(({ wallet, percentage }, i) => {
            return processSellTransaction(
              wallet,
              percentage,
              i + 1,
              currentBatch.length,
              false,
              actualSlippage,
              currentSellToken // 传递当前选择的卖出代币
            ).then(success => {
              if (success) {
                successCount++;
                // 从失败列表中移除成功的钱包
                failedWallets = failedWallets.filter(fw => fw.wallet.address !== wallet.address);
              }
            });
          });
          
          // 处理重试结果...与原代码保持一致
        }
      }
      
      if (transactionsPaused) {
        setError(`批量卖出已暂停: ${successCount}/${totalCount} 个钱包成功`);
      } else {
        setError(`批量卖出完成: ${successCount}/${totalCount} 个钱包成功`);
      }
    } catch (err) {
      handleError(err, "批量卖出");
    } finally {
      setBatchSellLoading(false);
    }
  };

  // 开始交易
  const startVolumeTrading = async () => {
    try {
      // 设置交易状态为活跃
      setIsVolumeTrading(true);
      
      // 验证参数
      if (!volumeRounds || volumeRounds <= 0) {
        message.error('请输入有效的交易轮数');
      return;
    }

      // 清空上一次的状态
      setVolumeTradeResults({
        isRunning: true,
        totalBought: 0,
        totalSold: 0,
        totalTransactions: 0,
        roundsCompleted: 0,
        startTime: new Date(),
        buyBatchResults: [],
        sellBatchResults: []
      });

      // 获取当前选择的代币和其他设置
      const currentBuyToken = volumeBuyToken; // 当前选择的买入代币
      const currentSellToken = volumeSellToken; // 当前选择的卖出代币
      
      const totalRounds = parseInt(volumeRounds);
      const buyWallets = selectedWallets.filter(wallet => wallet.selected && wallet.operations.buy);
      const sellWallets = selectedWallets.filter(wallet => wallet.selected && wallet.operations.sell);
      const buyPercentage = parseFloat(volumeBuyPercentage);
      const sellPercentage = parseFloat(volumeSellPercentage);

      if (buyWallets.length === 0 && sellWallets.length === 0) {
        message.error('请至少选择一个钱包进行买入或卖出操作');
        resetVolumeTradeResults();
        return;
      }

      if ((buyWallets.length > 0 && (isNaN(buyPercentage) || buyPercentage <= 0)) || 
          (sellWallets.length > 0 && (isNaN(sellPercentage) || sellPercentage <= 0))) {
        message.error('请输入有效的买入/卖出百分比');
        resetVolumeTradeResults();
        return;
      }

      // 显示交易开始消息
      message.info(`开始做市交易: ${totalRounds}轮, 买入使用${currentBuyToken}, 卖出获得${currentSellToken}`);
      
      // 保存开始时间
      const startTime = new Date();
      let roundsCompleted = 0;
      
      // 执行每一轮交易
      for (let round = 1; round <= totalRounds; round++) {
        if (shouldStopVolumeTrading) {
          console.log('做市交易被手动停止');
          break;
        }

        setVolumeTradeStatus(`第 ${round}/${totalRounds} 轮交易`);
        
        let buyResults = [];
        let sellResults = [];
        
        // 批量买入处理
        if (buyWallets.length > 0) {
          setVolumeTradeStatus(`第 ${round}/${totalRounds} 轮 - 执行买入`);
          
          // 根据批处理设置决定并行或顺序处理
          if (volumeBuySequential) {
            // 顺序处理每个钱包
            for (let i = 0; i < buyWallets.length; i++) {
              const wallet = buyWallets[i];
              
              // 计算买入金额
              let buyAmount;
              if (volumeBuyRandom) {
                // 随机金额: 在 minBuyAmount 和 maxBuyAmount 之间
                const min = parseFloat(minBuyAmount);
                const max = parseFloat(maxBuyAmount);
                buyAmount = (Math.random() * (max - min) + min).toFixed(6);
            } else {
                buyAmount = volumeBuyAmount;
              }
              
              // 执行买入交易
              const success = await processBuyTransaction(
                wallet,
                buyAmount, 
                i + 1, 
                buyWallets.length,
                true, // 是做市交易
                volumeBuySlippage,
                currentBuyToken // 传递买入代币类型
              );
              
              buyResults.push({
                wallet: wallet.address,
                amount: buyAmount,
                success
              });
              
              // 添加随机延迟
              if (i < buyWallets.length - 1) {
                const delay = Math.floor(Math.random() * (maxDelay - minDelay) + minDelay);
                await new Promise(resolve => setTimeout(resolve, delay));
              }
            }
          } else {
            // 并行处理所有钱包
            const buyPromises = buyWallets.map(async (wallet, index) => {
              // 计算买入金额
              let buyAmount;
              if (volumeBuyRandom) {
                const min = parseFloat(minBuyAmount);
                const max = parseFloat(maxBuyAmount);
                buyAmount = (Math.random() * (max - min) + min).toFixed(6);
              } else {
                buyAmount = volumeBuyAmount;
              }
              
              // 执行买入交易
              const success = await processBuyTransaction(
          wallet,
                buyAmount, 
                index + 1, 
                buyWallets.length,
          true,
                volumeBuySlippage,
                currentBuyToken // 传递买入代币类型
              );
              
              return {
                wallet: wallet.address,
                amount: buyAmount,
                success
              };
            });
            
            buyResults = await Promise.all(buyPromises);
          }
          
          // 汇总批处理结果
          const successfulBuys = buyResults.filter(r => r.success);
          const totalBought = successfulBuys.reduce((sum, r) => sum + parseFloat(r.amount), 0);
          
          // 更新交易结果
          setVolumeTradeResults(prev => ({
            ...prev,
            totalBought: prev.totalBought + totalBought,
            totalTransactions: prev.totalTransactions + successfulBuys.length,
            buyBatchResults: [...prev.buyBatchResults, {
              round,
              transactions: successfulBuys.length,
              amount: totalBought,
              timestamp: new Date()
            }]
          }));
        }
        
        // 买卖之间的延迟
        const betweenDelay = Math.floor(Math.random() * (maxDelay - minDelay) + minDelay);
        await new Promise(resolve => setTimeout(resolve, betweenDelay));
        
        // 批量卖出处理
        if (sellWallets.length > 0) {
          setVolumeTradeStatus(`第 ${round}/${totalRounds} 轮 - 执行卖出`);
          
          // 根据批处理设置决定并行或顺序处理
          if (volumeSellSequential) {
            // 顺序处理每个钱包
            for (let i = 0; i < sellWallets.length; i++) {
              const wallet = sellWallets[i];
              
              // 执行卖出交易，使用设定的卖出比例
              const success = await processSellTransaction(
                wallet,
                volumeSellPercentage, 
                i + 1,
                sellWallets.length,
                true,
                volumeSellSlippage,
                currentSellToken // 传递卖出代币类型
              );
              
              sellResults.push({
                wallet: wallet.address,
                percentage: volumeSellPercentage,
                success
              });
              
              // 添加随机延迟
              if (i < sellWallets.length - 1) {
                const delay = Math.floor(Math.random() * (maxDelay - minDelay) + minDelay);
                await new Promise(resolve => setTimeout(resolve, delay));
              }
            }
            } else {
            // 并行处理所有钱包
            const sellPromises = sellWallets.map(async (wallet, index) => {
              // 执行卖出交易
              const success = await processSellTransaction(
                wallet,
                volumeSellPercentage, 
                index + 1, 
                sellWallets.length,
                true,
                volumeSellSlippage,
                currentSellToken // 传递卖出代币类型
              );
              
              return {
                wallet: wallet.address,
                percentage: volumeSellPercentage,
                success
              };
            });
            
            sellResults = await Promise.all(sellPromises);
          }
          
          // 汇总批处理结果
          const successfulSells = sellResults.filter(r => r.success);
          
          // 更新交易结果
          setVolumeTradeResults(prev => ({
            ...prev,
            totalTransactions: prev.totalTransactions + successfulSells.length,
            sellBatchResults: [...prev.sellBatchResults, {
              round,
              transactions: successfulSells.length,
              timestamp: new Date()
            }]
          }));
        }
        
        // 更新完成的轮数
        roundsCompleted = round;
        setVolumeTradeResults(prev => ({
          ...prev,
          roundsCompleted: round
        }));
        
        // 轮次之间的延迟
        if (round < totalRounds) {
          const roundDelay = Math.floor(Math.random() * (maxRoundDelay - minRoundDelay) + minRoundDelay);
          setVolumeTradeStatus(`等待下一轮 (${Math.floor(roundDelay / 1000)}秒)`);
          await new Promise(resolve => setTimeout(resolve, roundDelay));
        }
      }
      
      // 计算总时间
      const endTime = new Date();
      const totalSeconds = Math.floor((endTime - startTime) / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      const timeString = `${hours > 0 ? hours + '小时 ' : ''}${minutes}分钟 ${seconds}秒`;
      
      // 更新最终结果
      setVolumeTradeResults(prev => ({
        ...prev,
        isRunning: false,
        endTime,
        duration: timeString,
        roundsCompleted
      }));
      
      // 重置交易状态
      setIsVolumeTrading(false);
      
      setVolumeTradeStatus(`做市交易完成: ${roundsCompleted}/${totalRounds}轮, 耗时${timeString}`);
      message.success(`做市交易已完成: ${roundsCompleted}/${totalRounds}轮, 耗时${timeString}`);
      
      // 重置停止标志
      setShouldStopVolumeTrading(false);
    } catch (err) {
      console.error('做市交易出错:', err);
      message.error(`做市交易出错: ${err.message}`);
      
      // 更新错误状态
      setVolumeTradeResults(prev => ({
        ...prev,
        isRunning: false,
        error: err.message
      }));
      
      setVolumeTradeStatus('做市交易出错: ' + err.message);
      
      // 重置停止标志
      setShouldStopVolumeTrading(false);
    } finally {
      // 确保交易状态被重置
      setIsVolumeTrading(false);
    }
  };

  // 停止做市交易
  const stopVolumeTrading = () => {
    setIsVolumeTrading(false);
    setShouldStopVolumeTrading(true);
    setError("做市交易已停止");
  };

  // 添加删除钱包函数
  const deleteWallet = (address) => {
    setWallets(prev => prev.filter(w => w.address !== address));
    setSelectedWallets(prev => prev.filter(w => w.address !== address));
  };

  // 添加暂停所有交易的函数
  const pauseAllTransactions = () => {
    setTransactionsPaused(true);
    setError("所有交易已暂停");
    // 清除活动交易状态
    setActiveTx(null);
  };

  // 添加恢复交易的函数
  const resumeAllTransactions = () => {
    setTransactionsPaused(false);
    setError("交易已恢复");
  };

  // 处理代币授权
  const handleTokenApproval = async (tokenContract, spenderAddress, walletAddress, gasPrice) => {
    setActiveTx(prev => ({
      ...prev,
      status: '正在授权'
    }));

    try {
      const maxUint256 = ethers.MaxUint256;
      const approveTx = await tokenContract.approve(spenderAddress, maxUint256, {
        gasLimit: 100000,
        gasPrice: gasPrice.gasPrice
      });

      setActiveTx(prev => ({
        ...prev,
        status: '等待授权确认'
      }));

      // 轮询方式检查授权确认状态
      let approveReceipt = null;
      let approveAttempts = 0;
      const maxApproveAttempts = 60; // 最多等待60秒

      while (approveAttempts < maxApproveAttempts) {
        try {
          approveReceipt = await rpcProvider.getTransactionReceipt(approveTx.hash);

          if (approveReceipt && approveReceipt.blockNumber) {
            break;
          }

          setActiveTx(prev => ({
            ...prev,
            status: `等待授权确认 (${approveAttempts}秒)`
          }));

          await new Promise(resolve => setTimeout(resolve, 1000));
          approveAttempts++;

        } catch (err) {
          console.error("检查授权确认状态失败:", err);
          await new Promise(resolve => setTimeout(resolve, 1000));
          approveAttempts++;
        }
      }

      if (!approveReceipt) {
        throw new Error('授权交易超时，请检查链上状态');
      }

      if (approveReceipt.status === 1) {
        await new Promise(resolve => setTimeout(resolve, 3000));
        const newAllowance = await tokenContract.allowance(walletAddress, spenderAddress);
        
        if (newAllowance < ethers.MaxUint256) {
          throw new Error(`授权失败，新授权额度不足`);
        }
      } else {
        throw new Error(`授权交易失败`);
      }
    } catch (approveErr) {
      throw new Error(`代币授权失败: ${approveErr.message}`);
    }
  };

  // 渲染UI组件
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          PancakeSwap V2 批量交易
        </Typography>
        
        {/* 添加RPC配置部分，使其更加突出 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            RPC节点配置
          </Typography>
          <Paper sx={{ p: 2 }}>
            <FormControl fullWidth>
              <InputLabel>选择 RPC 节点</InputLabel>
              <Select
                value={selectedRpc.url}
                onChange={(e) => {
                  const rpc = RPC_URLS.find(r => r.url === e.target.value);
                  setSelectedRpc(rpc);
                  setCurrentRpcUrl(rpc.url);
                }}
                label="选择 RPC 节点"
              >
                {RPC_URLS.map((rpc) => (
                  <MenuItem key={rpc.url} value={rpc.url}>
                    {rpc.name} - {rpc.url}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              使用RPC节点查询余额和价格，无需连接钱包
            </Typography>
          </Paper>
        </Box>
        
        {/* 代币信息 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            代币信息
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                label="代币地址"
                value={tokenAddress}
                onChange={(e) => setTokenAddress(e.target.value)}
                fullWidth
                error={!!error && error.includes("代币地址")}
                helperText={error && error.includes("代币地址") ? error : ""}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>交易对</InputLabel>
                <Select
                  value={selectedPair.symbol}
                  onChange={(e) => {
                    const pair = TRADING_PAIRS.find(p => p.symbol === e.target.value);
                    setSelectedPair(pair);
                    if (tokenAddress) {
                      fetchTokenInfo();
                    }
                  }}
                  label="交易对"
                >
                  {TRADING_PAIRS.map((pair) => (
                    <MenuItem key={pair.symbol} value={pair.symbol}>
                      {pair.symbol}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                onClick={fetchTokenInfo}
                disabled={loading || !tokenAddress || !rpcProvider}
                startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                fullWidth
              >
                {loading ? "获取中..." : "获取代币信息"}
              </Button>
            </Grid>
          </Grid>
          {tokenInfo.name && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                代币详情
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    名称
                  </Typography>
                  <Typography variant="body1">
                    {tokenInfo.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    符号
                  </Typography>
                  <Typography variant="body1">
                    {tokenInfo.symbol}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    精度
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    fontFamily: 'monospace', 
                    fontSize: '1.1rem',
                    whiteSpace: 'nowrap',
                    overflow: 'visible'
                  }}>
                    {tokenInfo.decimals}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    合约地址
                  </Typography>
                  <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                    {tokenAddress}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}
        </Box>

        {/* 价格信息 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            价格信息
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      USDT价格
                    </Typography>
                    <Box sx={{ 
                      width: '100%',
                      maxWidth: 'none',
                      overflow: 'visible',
                      position: 'relative'
                    }}>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontFamily: 'monospace', 
                          fontSize: '1.1rem',
                          whiteSpace: 'nowrap',
                          overflow: 'visible',
                          textOverflow: 'clip',
                          width: 'auto',
                          position: 'absolute',
                          left: 0,
                          right: 'auto',
                          display: 'inline-block'
                        }}
                      >
                        {priceInfo.usdtPrice}
                      </Typography>
                    </Box>
                    <Box sx={{ visibility: 'hidden', height: '2rem' }} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {selectedPair.symbol}价格
                    </Typography>
                    <Box sx={{ 
                      width: 'auto',
                      minWidth: '200px',
                      overflow: 'visible',
                      position: 'relative'
                    }}>
                      <Typography variant="body1" component="div" sx={{ 
                        fontFamily: 'monospace', 
                        fontSize: '1.1rem',
                        whiteSpace: 'nowrap',
                        overflow: 'visible',
                        textOverflow: 'unset',
                        width: 'auto',
                        position: 'absolute',
                        left: 0,
                        right: 'auto'
                      }}>
                        {priceInfo.price}
                      </Typography>
                    </Box>
                    <Box sx={{ visibility: 'hidden', height: '2rem' }} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      FIST-USDT价格
                    </Typography>
                    <Box sx={{ 
                      width: 'auto',
                      minWidth: '200px',
                      overflow: 'visible',
                      position: 'relative'
                    }}>
                      <Typography variant="body1" component="div" sx={{ 
                        fontFamily: 'monospace', 
                        fontSize: '1.1rem',
                        whiteSpace: 'nowrap',
                        overflow: 'visible',
                        textOverflow: 'unset',
                        width: 'auto',
                        position: 'absolute',
                        left: 0,
                        right: 'auto'
                      }}>
                        {priceInfo.fistUsdtRate}
                      </Typography>
                    </Box>
                    <Box sx={{ visibility: 'hidden', height: '2rem' }} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      流动性 ({selectedPair.symbol})
                    </Typography>
                    <Box sx={{ 
                      width: 'auto',
                      minWidth: '200px',
                      overflow: 'visible'
                    }}>
                      <Typography variant="body1" sx={{ 
                        fontFamily: 'monospace', 
                        fontSize: '1.1rem',
                        whiteSpace: 'nowrap',
                        overflow: 'visible',
                        textOverflow: 'unset',
                        width: 'auto'
                      }}>
                        {priceInfo.liquidity}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    最后更新: {priceInfo.timestamp}
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={fetchPriceAndLiquidity}
                    disabled={fetchTokenLoading}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* 钱包列表 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            钱包列表 (无需连接钱包)
          </Typography>
          {/* 添加总余额统计显示 */}
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle1" gutterBottom>
              总余额统计
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  总 BNB 余额
                </Typography>
                <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
                  {totalBalances.bnb || "0"} BNB
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  总 {tokenInfo.symbol || '代币'} 余额
                </Typography>
                <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
                  {totalBalances.token || "0"} {tokenInfo.symbol || '代币'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  总 FIST 余额
                </Typography>
                <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
                  {totalBalances.fist || "0"} FIST
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  代币总价值 (USDT)
                </Typography>
                <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
                  {totalBalances.tokenUsdtValue || "0"} USDT
                </Typography>
              </Grid>
            </Grid>
          </Paper>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="私钥列表"
                multiline
                rows={4}
                value={privateKeys}
                onChange={(e) => setPrivateKeys(e.target.value)}
                fullWidth
                helperText="每行一个私钥，无需连接钱包即可导入"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                variant="contained"
                color="primary"
                onClick={importPrivateKeys}
                disabled={importLoading || !privateKeys.trim() || !rpcProvider}
                fullWidth
              >
                {importLoading ? "导入中..." : "导入私钥"}
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                variant="contained"
                color="secondary"
                onClick={checkWalletBalances}
                disabled={updateBalanceLoading || wallets.length === 0 || !rpcProvider}
                fullWidth
              >
                {updateBalanceLoading ? "更新中..." : "更新余额"}
              </Button>
            </Grid>
          </Grid>

          {/* 添加钱包列表表格 */}
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={wallets.length > 0 && wallets.every(w => w.isSelected)}
                      indeterminate={wallets.some(w => w.isSelected) && !wallets.every(w => w.isSelected)}
                      onChange={(e) => {
                        const newWallets = wallets.map(w => ({
                          ...w,
                          isSelected: e.target.checked
                        }));
                        setWallets(newWallets);
                        setSelectedWallets(e.target.checked ? newWallets : []);
                      }}
                    />
                  </TableCell>
                  <TableCell>钱包地址</TableCell>
                  <TableCell align="right">BNB余额</TableCell>
                  <TableCell align="right">代币余额</TableCell>
                  <TableCell align="right">FIST余额</TableCell>
                  <TableCell align="right">代币价值(USDT)</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {wallets.map((wallet, index) => {
                  const tokenUsdtValue = calculateTokenUsdtValue(
                    parseFloat(wallet.tokenBalance),
                    tokenInfo.symbol,
                    selectedPair.symbol,
                    priceInfo
                  ).toFixed(2);
                  
                  return (
                    <TableRow key={index}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={wallet.isSelected}
                          onChange={(e) => {
                            const newWallets = [...wallets];
                            newWallets[index] = {
                              ...wallet,
                              isSelected: e.target.checked
                            };
                            setWallets(newWallets);
                            setSelectedWallets(newWallets.filter(w => w.isSelected));
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {wallet.address}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {wallet.bnbBalance || '0'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {wallet.tokenBalance || '0'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {wallet.fistBalance || '0'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {wallet.tokenUsdtValue || '0.00'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => deleteWallet(wallet.address)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      
        {/* 交易功能 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            交易功能
          </Typography>
          <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="body1" gutterBottom>
              注意：所有交易将使用您导入的私钥进行，无需连接钱包。
              您也可以选择连接钱包以便在MetaMask中查看交易内容。
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              onClick={connectWallet}
              disabled={loading}
              sx={{ mr: 2 }}
            >
              {connectLoading ? "连接中..." : "连接钱包(可选)"}
            </Button>
            {connectedAddress && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                已连接: {connectedAddress}
              </Typography>
            )}
          </Paper>
        </Box>

        {/* 以下内容继续保持原样 */}
        
        {/* 交易设置 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            交易设置
          </Typography>
          <Grid container spacing={2}>
            {/* 添加GAS设置 */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  GAS设置
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useCustomGas}
                      onChange={(e) => setUseCustomGas(e.target.checked)}
                    />
                  }
                  label="使用自定义GAS"
                  sx={{ mb: 2 }}
                />
                {useCustomGas && (
                  <TextField
                    label="GAS价格 (Gwei)"
                    value={customGasPrice}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        setCustomGasPrice(value);
                      }
                    }}
                    fullWidth
                    sx={{ mb: 2 }}
                    helperText="留空则使用默认GAS价格"
                  />
                )}
              </Paper>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  买入设置
                </Typography>
                
                {/* 添加买入币种选择 */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>用什么购买</InputLabel>
                  <Select
                    value={buyToken}
                    onChange={(e) => setBuyToken(e.target.value)}
                    label="用什么购买"
                  >
                    <MenuItem value="BNB">BNB</MenuItem>
                    <MenuItem value="FIST">FIST</MenuItem>
                    <MenuItem value="USDT">USDT</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  label={`${buyToken}买入数量`}
                  value={buyAmount}
                  onChange={(e) => setBuyAmount(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                  helperText={`使用${buyToken}购买目标代币的数量`}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={useRandomBuyAmount}
                      onChange={(e) => setUseRandomBuyAmount(e.target.checked)}
                    />
                  }
                  label="随机买入数量"
                  sx={{ mb: 2 }}
                />
                {useRandomBuyAmount && (
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      label={`最小${buyToken}数量`}
                      value={buyAmountRange.min}
                      onChange={(e) => setBuyAmountRange(prev => ({ ...prev, min: e.target.value }))}
                      sx={{ mr: 2 }}
                    />
                    <TextField
                      label={`最大${buyToken}数量`}
                      value={buyAmountRange.max}
                      onChange={(e) => setBuyAmountRange(prev => ({ ...prev, max: e.target.value }))}
                    />
                  </Box>
                )}
                <TextField
                  label="滑点 (%)"
                  value={buySlippage}
                  onChange={(e) => setBuySlippage(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={buySequentialMode}
                      onChange={(e) => setBuySequentialMode(e.target.checked)}
                    />
                  }
                  label="顺序执行"
                  sx={{ mb: 2 }}
                />
                {buySequentialMode && (
                  <TextField
                    label="交易间隔 (秒)"
                    value={buyDelayBetweenTx}
                    onChange={(e) => setBuyDelayBetweenTx(Number(e.target.value))}
                    fullWidth
                  />
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  卖出设置
                </Typography>
                
                {/* 添加卖出币种选择 */}
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>获得什么代币</InputLabel>
                  <Select
                    value={sellToken}
                    onChange={(e) => setSellToken(e.target.value)}
                    label="获得什么代币"
                  >
                    <MenuItem value="BNB">BNB</MenuItem>
                    <MenuItem value="FIST">FIST</MenuItem>
                    <MenuItem value="USDT">USDT</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  label="卖出比例 (%)"
                  value={sellAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
                      setSellAmount(value);
                    }
                  }}
                  fullWidth
                  sx={{ mb: 2 }}
                  helperText={`输入0-100之间的数字，表示要卖出的代币百分比，将获得${sellToken}`}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={useRandomSellAmount}
                      onChange={(e) => setUseRandomSellAmount(e.target.checked)}
                    />
                  }
                  label="随机卖出比例"
                  sx={{ mb: 2 }}
                />
                {useRandomSellAmount && (
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      label="最小比例 (%)"
                      value={sellAmountRange.min}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
                          setSellAmountRange(prev => ({ ...prev, min: value }));
                        }
                      }}
                      sx={{ mr: 2 }}
                      helperText="0-100"
                    />
                    <TextField
                      label="最大比例 (%)"
                      value={sellAmountRange.max}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= 100)) {
                          setSellAmountRange(prev => ({ ...prev, max: value }));
                        }
                      }}
                      helperText="0-100"
                    />
                  </Box>
                )}
                <TextField
                  label="滑点 (%)"
                  value={sellSlippage}
                  onChange={(e) => setSellSlippage(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={sellSequentialMode}
                      onChange={(e) => setSellSequentialMode(e.target.checked)}
                    />
                  }
                  label="顺序执行"
                  sx={{ mb: 2 }}
                />
                {sellSequentialMode && (
                  <TextField
                    label="交易间隔 (秒)"
                    value={sellDelayBetweenTx}
                    onChange={(e) => setSellDelayBetweenTx(Number(e.target.value))}
                    fullWidth
                  />
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* 做市交易设置 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            做市交易设置
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowVolumeSettings(!showVolumeSettings)}
            sx={{ mb: 2 }}
          >
            {showVolumeSettings ? '隐藏设置' : '显示设置'}
          </Button>
          
          <Collapse in={showVolumeSettings}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    做市买入设置
                  </Typography>
                  
                  {/* 添加做市买入币种选择 */}
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>用什么购买</InputLabel>
                    <Select
                      value={volumeBuyToken}
                      onChange={(e) => setVolumeBuyToken(e.target.value)}
                      label="用什么购买"
                    >
                      <MenuItem value="BNB">BNB</MenuItem>
                      <MenuItem value="FIST">FIST</MenuItem>
                      <MenuItem value="USDT">USDT</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <TextField
                    label={`${volumeBuyToken}买入数量`}
                    value={volumeBuyAmount}
                    onChange={(e) => setVolumeBuyAmount(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                    helperText={`使用${volumeBuyToken}购买目标代币的数量`}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={useVolumeRandomBuyAmount}
                        onChange={(e) => setUseVolumeRandomBuyAmount(e.target.checked)}
                      />
                    }
                    label="随机买入数量"
                    sx={{ mb: 2 }}
                  />
                  {useVolumeRandomBuyAmount && (
                    <Box sx={{ mb: 2 }}>
                      <TextField
                        label={`最小${volumeBuyToken}数量`}
                        value={volumeBuyAmountRange.min}
                        onChange={(e) => setVolumeBuyAmountRange(prev => ({ ...prev, min: e.target.value }))}
                        sx={{ mr: 2 }}
                      />
                      <TextField
                        label={`最大${volumeBuyToken}数量`}
                        value={volumeBuyAmountRange.max}
                        onChange={(e) => setVolumeBuyAmountRange(prev => ({ ...prev, max: e.target.value }))}
                      />
                    </Box>
                  )}
                  <TextField
                    label="滑点 (%)"
                    value={volumeBuySlippage}
                    onChange={(e) => setVolumeBuySlippage(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={volumeBuySequentialMode}
                        onChange={(e) => setVolumeBuySequentialMode(e.target.checked)}
                      />
                    }
                    label="顺序执行"
                    sx={{ mb: 2 }}
                  />
                  {volumeBuySequentialMode && (
                    <TextField
                      label="交易间隔 (秒)"
                      value={volumeBuyDelayBetweenTx}
                      onChange={(e) => setVolumeBuyDelayBetweenTx(Number(e.target.value))}
                      fullWidth
                    />
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    做市卖出设置
                  </Typography>
                  
                  {/* 添加做市卖出币种选择 */}
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>获得什么代币</InputLabel>
                    <Select
                      value={volumeSellToken}
                      onChange={(e) => setVolumeSellToken(e.target.value)}
                      label="获得什么代币"
                    >
                      <MenuItem value="BNB">BNB</MenuItem>
                      <MenuItem value="FIST">FIST</MenuItem>
                      <MenuItem value="USDT">USDT</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <TextField
                    label="卖出比例 (%)"
                    value={volumeSellAmount}
                    onChange={(e) => setVolumeSellAmount(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                    helperText={`输入0-100之间的数字，表示要卖出的代币百分比，将获得${volumeSellToken}`}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={useVolumeRandomSellAmount}
                        onChange={(e) => setUseVolumeRandomSellAmount(e.target.checked)}
                      />
                    }
                    label="随机卖出比例"
                    sx={{ mb: 2 }}
                  />
                  {useVolumeRandomSellAmount && (
                    <Box sx={{ mb: 2 }}>
                      <TextField
                        label="最小比例 (%)"
                        value={volumeSellAmountRange.min}
                        onChange={(e) => setVolumeSellAmountRange(prev => ({ ...prev, min: e.target.value }))}
                        sx={{ mr: 2 }}
                        helperText="0-100"
                      />
                      <TextField
                        label="最大比例 (%)"
                        value={volumeSellAmountRange.max}
                        onChange={(e) => setVolumeSellAmountRange(prev => ({ ...prev, max: e.target.value }))}
                        helperText="0-100"
                      />
                    </Box>
                  )}
                  <TextField
                    label="滑点 (%)"
                    value={volumeSellSlippage}
                    onChange={(e) => setVolumeSellSlippage(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={volumeSellSequentialMode}
                        onChange={(e) => setVolumeSellSequentialMode(e.target.checked)}
                      />
                    }
                    label="顺序执行"
                    sx={{ mb: 2 }}
                  />
                  {volumeSellSequentialMode && (
                    <TextField
                      label="交易间隔 (秒)"
                      value={volumeSellDelayBetweenTx}
                      onChange={(e) => setVolumeSellDelayBetweenTx(Number(e.target.value))}
                      fullWidth
                    />
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    做市交易参数
                  </Typography>
                  <TextField
                    label="交易轮数"
                    value={volumeRounds}
                    onChange={(e) => setVolumeRounds(Number(e.target.value))}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2">买入比例</Typography>
                      <Slider
                        value={buyPercentage}
                        onChange={(e, newValue) => setBuyPercentage(newValue)}
                        valueLabelDisplay="auto"
                        step={1}
                        marks
                        min={0}
                        max={100}
                      />
                      <Typography variant="body2" align="center">
                        {buyPercentage}%
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2">卖出比例</Typography>
                      <Slider
                        value={sellPercentage}
                        onChange={(e, newValue) => setSellPercentage(newValue)}
                        valueLabelDisplay="auto"
                        step={1}
                        marks
                        min={0}
                        max={100}
                      />
                      <Typography variant="body2" align="center">
                        {sellPercentage}%
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Collapse>
        </Box>

        {/* 交易操作 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            交易操作
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={batchBuy}
            disabled={batchBuyLoading || !rpcProvider || selectedWallets.length === 0 || !tokenAddress}
            sx={{ mr: 2 }}
          >
            {batchBuyLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                批量买入中...
              </Box>
            ) : (
              `批量买入 (使用${buyToken})`
            )}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={batchSell}
            disabled={batchSellLoading || !rpcProvider || selectedWallets.length === 0 || !tokenAddress}
            sx={{ mr: 2 }}
          >
            {batchSellLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                批量卖出中...
              </Box>
            ) : (
              `批量卖出 (获得${sellToken})`
            )}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={startVolumeTrading}
            disabled={volumeTradingLoading || !rpcProvider || selectedWallets.length === 0 || isVolumeTrading || !tokenAddress}
            sx={{ mr: 2 }}
          >
            {volumeTradingLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                启动中...
              </Box>
            ) : (
              `开始做市 (使用${volumeBuyToken}/获得${volumeSellToken})`
            )}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={stopVolumeTrading}
            disabled={!isVolumeTrading}
            sx={{ mr: 2 }}
          >
            停止做市
          </Button>
          
          {/* 添加交易暂停/恢复按钮 */}
          {transactionsPaused ? (
            <Button
              variant="contained"
              color="success"
              onClick={resumeAllTransactions}
              sx={{ mr: 2 }}
            >
              恢复交易
            </Button>
          ) : (
            <Button
              variant="contained"
              color="error"
              onClick={pauseAllTransactions}
              sx={{ mr: 2 }}
            >
              暂停所有交易
            </Button>
          )}
        </Box>

        {/* 交易状态 */}
        {(activeTx || transactionsPaused) && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              当前交易
            </Typography>
            <Paper sx={{ p: 2 }}>
              {transactionsPaused && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  交易已暂停 - 所有后续交易都已停止
                </Alert>
              )}
              
              {activeTx && (
                <>
                  <Typography variant="body2">
                    钱包: {activeTx.wallet}
                  </Typography>
                  <Typography variant="body2">
                    类型: {activeTx.type}
                  </Typography>
                  <Typography variant="body2">
                    数量: {activeTx.amount}
                  </Typography>
                  <Typography variant="body2">
                    状态: {activeTx.status}
                  </Typography>
                  <Typography variant="body2">
                    进度: {activeTx.index}/{activeTx.total}
                  </Typography>
                </>
              )}
            </Paper>
          </Box>
        )}

        {/* 做市交易状态 */}
        {isVolumeTrading && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              做市交易状态
            </Typography>
            <Paper sx={{ p: 2 }}>
              <Typography variant="body2">
                状态: {volumeTradeStatus}
              </Typography>
            </Paper>
          </Box>
        )}

        {/* 交易历史 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            交易历史
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>时间</TableCell>
                  <TableCell>钱包</TableCell>
                  <TableCell>类型</TableCell>
                  <TableCell>支付</TableCell>
                  <TableCell>接收</TableCell>
                  <TableCell>状态</TableCell>
                  <TableCell>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {txHistory.map((tx, index) => (
                  <TableRow key={index}>
                    <TableCell>{tx.timestamp}</TableCell>
                    <TableCell>{tx.wallet}</TableCell>
                    <TableCell>{tx.type}</TableCell>
                    <TableCell>
                      {tx.spentAmount} {tx.spentSymbol}
                    </TableCell>
                    <TableCell>
                      {tx.receivedAmount} {tx.receivedSymbol}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tx.status}
                        color={tx.status === '已完成' ? 'success' : tx.status === '失败' ? 'error' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {tx.explorerUrl && (
                        <Button
                          variant="outlined"
                          size="small"
                          href={tx.explorerUrl}
                          target="_blank"
                        >
                          查看
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* 交易状态显示 */}
        {transactionStatus.isProcessing && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              交易状态
            </Typography>
            <Paper sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">
                    当前操作: {transactionStatus.currentOperation}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={(transactionStatus.progress / transactionStatus.total) * 100}
                    sx={{ mt: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    进度: {transactionStatus.progress}/{transactionStatus.total}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="success.main">
                    成功: {transactionStatus.successCount}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="error.main">
                    失败: {transactionStatus.failCount}
                  </Typography>
                </Grid>
                {transactionStatus.lastError && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="error">
                      最后错误: {transactionStatus.lastError}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Box>
        )}

        {/* 错误提示 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* 加载状态 */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress />
          </Box>
        )}

        {/* 确认对话框 */}
        <Dialog
          open={showConfirmDialog}
          onClose={() => setShowConfirmDialog(false)}
        >
          <DialogTitle>确认操作</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {pendingAction?.type === 'rebalance' && (
                <>
                  您确定要重新分配钱包余额吗？
                  <br />
                  这将从余额充足的钱包转入资金到余额不足的钱包。
                </>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowConfirmDialog(false)}>取消</Button>
            <Button
              onClick={() => {
                if (pendingAction?.type === 'rebalance') {
                  // 执行余额重新分配
                  const { sufficientWallets, insufficientWallets, MIN_BNB_BALANCE, TARGET_BNB_BALANCE } = pendingAction;
                  // ... 实现余额重新分配逻辑
                }
                setShowConfirmDialog(false);
              }}
              color="primary"
            >
              确认
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
}

export default PancakeV2BatchSwap;